// SPDX-License-Identifier: Apache-2.0
// File: src/ui/components/MainPerformanceView.tsx
import React, { useEffect, useRef, useState } from 'react';
import { synthEngine, type TelemetryFrame } from '../../audio/AudioEngineController';
import {
  loadParamToCcMappings,
  webMidiController,
  type MidiDeviceStatus,
} from '../../audio/WebMidiController';
import {
  calculateWarpStepDurationMs,
  loadWorkspaceLayoutFromStorage,
  saveWorkspaceLayoutToStorage,
} from '../../io/SysexAndExportPipeline';
import {
  FACTORY_PRESETS,
  INSTRUMENT_CATEGORIES,
  loadPresetsWithUserSlots,
  MUSICAL_SCALES,
  quantizeNoteToScale,
  type SynthPreset,
} from '../../presets/FactorySoundLibrary';
import {
  ALL_ASSIGNABLE_PARAMS,
  PocketGridRetroPalette,
  STUDIO_DEFAULT_SLIDER_BANKS,
  type SynthParamId,
} from '../theme/PocketGridRetroTheme';
import {
  ALL_SYNTH_PARAMS,
  DEEP_CHARACTER_PARAMS,
  expandPresetParamsToAllViews,
  getInitialMidiCcMap,
  getInitialParamValues,
  getSynchronizedParamUpdates,
  PERFORMANCE_FX_PARAMS,
  TOP_16_PARAMS,
  TOP_32_PARAMS,
  type ParamDefinition,
} from '../../audio/SynthParameters';
import { FindSoundsModal } from './FindSoundsModal';
import { FineTuneSoundModal } from './FineTuneSoundModal';
import { CustomChordModal } from './CustomChordModal';
import { SaveSequenceModal } from './SaveSequenceModal';
import { PrecisionHorizontalSlider } from './PrecisionHorizontalSlider';
import {
  type SmartChord,
  loadSavedSmartChords,
  saveSmartChordsToStorage,
  CHORD_ROOTS,
  type ChordRootName,
  ROOT_SEMITONES,
  buildSmartChord,
} from '../../audio/ChordLibrary';

const DRUM_LABELS = ['KICK', 'SNARE', 'C-HAT', 'O-HAT', 'TOM', 'PERC', 'CLAP', 'COWBELL', 'RIM', 'SHAKER'];
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const MAX_SEQ_STEPS = 21;
const SEQ_BANK_STORAGE_KEY = 'pocketgrid_saved_sequence_bank_v1';

function formatMidiNote(midi: number): string {
  const name = NOTE_NAMES[((midi % 12) + 12) % 12];
  const oct = Math.floor(midi / 12) - 1;
  return `${name}${oct}`;
}

// Each step space in the sequencer can hold either a single note OR a full polyphonic chord!
export interface SeqLineStep {
  active: boolean;
  notes: number[]; // 1 note for single-note step, or 3..5 notes for a full chord in a single space!
  chordLabel?: string; // e.g., 'Dm9', 'G13', 'Cmaj9', 'Am11' when a full chord is placed in this space
  velocity: number;
}

export interface SequencerTrackLine {
  id: number;
  label: string;
  kind: 'drum' | 'synth';
  drumIdx: number; // 0..9 when kind === 'drum'
  presetId: string; // Factory/user preset ID when kind === 'synth'
  defaultNotes: number[]; // Active note or full chord brush for this line
  defaultChordLabel?: string; // If a chord is selected as the brush for this line
  accentColor: string;
  muted: boolean;
  soloed: boolean;
  bus?: 'DRUMS' | 'SYNTHS'; // Sub-mix group bus for one-touch bulk mute/solo/clear
  steps: SeqLineStep[]; // Length 21 (uses 16 or 21 depending on stepCountMode)
  gateSteps?: number; // 1 = 1/4 beat (1 step), 2 = 1/2 beat, 4 = 1 beat, 8 = 2 beats
}

export interface SavedSequenceSlot {
  slotIndex: number; // 1..8
  name: string;
  stepCount: 16 | 21;
  bpm: number;
  isPopulated: boolean;
  lines: SequencerTrackLine[];
}

function createInitial8SequenceLines(variant: 'groove16' | 'chords16' | 'poly21' = 'groove16'): SequencerTrackLine[] {
  return [
    {
      id: 0,
      label: 'LINE 1: KICK',
      kind: 'drum',
      drumIdx: 0,
      presetId: 'drums-analog-waveguide-808',
      defaultNotes: [36],
      accentColor: PocketGridRetroPalette.tungstenAmber,
      muted: false,
      soloed: false,
      bus: 'DRUMS',
      steps: Array.from({ length: MAX_SEQ_STEPS }, (_, i) => ({
        active:
          variant === 'poly21'
            ? i % 3 === 0
            : i === 0 || i === 4 || i === 8 || i === 10 || i === 12 || i === 16,
        notes: [36],
        velocity: 0.95,
      })),
    },
    {
      id: 1,
      label: 'LINE 2: SNARE',
      kind: 'drum',
      drumIdx: 1,
      presetId: 'drums-analog-waveguide-808',
      defaultNotes: [38],
      accentColor: PocketGridRetroPalette.burntTerracotta,
      muted: false,
      soloed: false,
      bus: 'DRUMS',
      steps: Array.from({ length: MAX_SEQ_STEPS }, (_, i) => ({
        active: variant === 'poly21' ? i === 3 || i === 9 || i === 15 : i === 4 || i === 12 || i === 18,
        notes: [38],
        velocity: 0.92,
      })),
    },
    {
      id: 2,
      label: 'LINE 3: C-HAT',
      kind: 'drum',
      drumIdx: 2,
      presetId: 'drums-analog-waveguide-808',
      defaultNotes: [42],
      accentColor: PocketGridRetroPalette.champagneBrass,
      muted: false,
      soloed: false,
      bus: 'DRUMS',
      steps: Array.from({ length: MAX_SEQ_STEPS }, (_, i) => ({
        active: i % 2 === 0 || i === 7 || i === 15 || i === 20,
        notes: [42],
        velocity: i % 4 === 0 ? 0.88 : 0.68,
      })),
    },
    {
      id: 3,
      label: 'LINE 4: CLAP / PERC',
      kind: 'drum',
      drumIdx: 5,
      presetId: 'drums-analog-waveguide-808',
      defaultNotes: [39],
      accentColor: PocketGridRetroPalette.tungstenAmber,
      muted: false,
      soloed: false,
      bus: 'DRUMS',
      steps: Array.from({ length: MAX_SEQ_STEPS }, (_, i) => ({
        active: i === 14 || i === 15 || i === 20,
        notes: [39],
        velocity: 0.85,
      })),
    },
    {
      id: 4,
      label: 'LINE 5: SUB BASS',
      kind: 'synth',
      drumIdx: 0,
      presetId: 'bass-lately-solid-fm',
      defaultNotes: [38], // D2
      accentColor: PocketGridRetroPalette.vintageVfdSage,
      muted: false,
      soloed: false,
      bus: 'SYNTHS',
      steps: Array.from({ length: MAX_SEQ_STEPS }, (_, i) => {
        const bassPattern = [38, 38, 50, 38, 41, 41, 45, 38, 38, 50, 48, 45, 43, 41, 38, 45, 38, 41, 43, 45, 50];
        return {
          active: i === 0 || i === 2 || i === 6 || i === 8 || i === 11 || i === 14 || i === 17,
          notes: [bassPattern[i % bassPattern.length]],
          velocity: 0.88,
        };
      }),
    },
    {
      id: 5,
      label: 'LINE 6: CHORD KEYS',
      kind: 'synth',
      drumIdx: 0,
      presetId: 'keys-velvet-tine-84',
      // Default to a full Dm9 Chord brush so single steps trigger full lush chords!
      defaultNotes: [62, 69, 72, 76, 77],
      defaultChordLabel: 'Dm9',
      accentColor: PocketGridRetroPalette.vintageVfdSage,
      muted: false,
      soloed: false,
      bus: 'SYNTHS',
      steps: Array.from({ length: MAX_SEQ_STEPS }, (_, i) => {
        if (i === 0) {
          return { active: true, notes: [62, 69, 72, 76, 77], chordLabel: 'Dm9', velocity: 0.82 };
        }
        if (i === 4) {
          return { active: variant === 'chords16', notes: [67, 73, 76, 78, 83], chordLabel: 'G13', velocity: 0.82 };
        }
        if (i === 8) {
          return { active: true, notes: [60, 67, 71, 74, 76], chordLabel: 'Cmaj9', velocity: 0.82 };
        }
        if (i === 12 || i === 16) {
          return { active: true, notes: [69, 76, 79, 81, 84], chordLabel: 'Am11', velocity: 0.82 };
        }
        return { active: false, notes: [62, 69, 72, 76, 77], chordLabel: 'Dm9', velocity: 0.80 };
      }),
    },
    {
      id: 6,
      label: 'LINE 7: BRASS LEAD',
      kind: 'synth',
      drumIdx: 0,
      presetId: 'lead-cs80-ribbon-brass',
      defaultNotes: [62], // D4
      accentColor: PocketGridRetroPalette.champagneBrass,
      muted: false,
      soloed: false,
      bus: 'SYNTHS',
      steps: Array.from({ length: MAX_SEQ_STEPS }, (_, i) => {
        const leadNotes = [62, 65, 69, 67, 65, 62, 60, 57, 62, 65, 69, 72, 69, 67, 65, 62, 65, 69, 72, 74, 69];
        return {
          active: i === 6 || i === 14 || i === 19,
          notes: [leadNotes[i % leadNotes.length]],
          velocity: 0.82,
        };
      }),
    },
    {
      id: 7,
      label: 'LINE 8: BELL / CHOIR',
      kind: 'synth',
      drumIdx: 0,
      presetId: 'bells-tubular-cathedral',
      defaultNotes: [69], // A4
      accentColor: PocketGridRetroPalette.burntTerracotta,
      muted: false,
      soloed: false,
      bus: 'SYNTHS',
      steps: Array.from({ length: MAX_SEQ_STEPS }, (_, i) => {
        const bellNotes = [69, 72, 74, 69, 67, 65, 69, 72, 74, 76, 74, 72, 69, 67, 65, 62, 69, 72, 74, 76, 79];
        return {
          active: i === 4 || i === 10 || i === 18,
          notes: [bellNotes[i % bellNotes.length]],
          velocity: 0.78,
        };
      }),
    },
  ];
}

function loadSequenceBankFromStorage(): SavedSequenceSlot[] {
  const defaults: SavedSequenceSlot[] = [
    {
      slotIndex: 1,
      name: '1: Studio Groove (16)',
      stepCount: 16,
      bpm: 120.0,
      isPopulated: true,
      lines: createInitial8SequenceLines('groove16'),
    },
    {
      slotIndex: 2,
      name: '2: Dorian Chords (16)',
      stepCount: 16,
      bpm: 112.0,
      isPopulated: true,
      lines: createInitial8SequenceLines('chords16'),
    },
    {
      slotIndex: 3,
      name: '3: Odd Meter Jam (21)',
      stepCount: 21,
      bpm: 128.0,
      isPopulated: true,
      lines: createInitial8SequenceLines('poly21'),
    },
    ...[4, 5, 6, 7, 8].map((idx) => ({
      slotIndex: idx,
      name: `Slot ${idx}: Empty`,
      stepCount: 16 as const,
      bpm: 120.0,
      isPopulated: false,
      lines: createInitial8SequenceLines('groove16'),
    })),
  ];

  try {
    const raw = localStorage.getItem(SEQ_BANK_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as SavedSequenceSlot[];
    if (!Array.isArray(parsed) || parsed.length !== 8) return defaults;
    return parsed.map((slot) => ({
      ...slot,
      lines: slot.lines.map((l) => ({
        ...l,
        soloed: l.soloed ?? false,
        bus: l.bus ?? (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS'),
      })),
    }));
  } catch {
    return defaults;
  }
}

function saveSequenceBankToStorage(bank: SavedSequenceSlot[]): void {
  try {
    localStorage.setItem(SEQ_BANK_STORAGE_KEY, JSON.stringify(bank));
  } catch {
    // Ignore storage quota errors
  }
}

function getParamLabel(paramId: string): string {
  if (ALL_SYNTH_PARAMS[paramId]) {
    return ALL_SYNTH_PARAMS[paramId].name;
  }
  if ((ALL_ASSIGNABLE_PARAMS as any)[paramId]) {
    return (ALL_ASSIGNABLE_PARAMS as any)[paramId].label;
  }
  return paramId;
}

export const MainPerformanceView: React.FC = () => {
  // Preset & Browser State (including 8 Popular + 4 User Slots per category)
  const [presets, setPresets] = useState<SynthPreset[]>(() => loadPresetsWithUserSlots());
  const [activePreset, setActivePreset] = useState<SynthPreset>(() => loadPresetsWithUserSlots()[0]);
  const [isFindSoundsOpen, setIsFindSoundsOpen] = useState(false);

  // Synthesizer Control Surface & UI Architecture Specification (v1.0)
  const [mainControlSurfaceTab, setMainControlSurfaceTab] = useState<'CORE' | 'CHARACTER_DEEP'>('CORE');
  const [coreSurfaceMode, setCoreSurfaceMode] = useState<'TOP_16' | 'TOP_32'>('TOP_16');
  const [keybedLayoutMode, setKeybedLayoutMode] = useState<'5_AUDITION_KEYS' | 'FULL_24_KEYS'>('5_AUDITION_KEYS');
  // Orientation & Fullscreen States for Mobile vs Desktop Monitor Experience
  const [isPortrait, setIsPortrait] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerHeight > window.innerWidth && window.innerWidth < 850;
    }
    return false;
  });
  const [isCompactViewport, setIsCompactViewport] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerHeight < 520 || window.innerWidth < 920;
    }
    return false;
  });
  const [dismissPortraitWarning, setDismissPortraitWarning] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isFxHoldOn, setIsFxHoldOn] = useState<boolean>(false);
  const [performanceFxValues, setPerformanceFxValues] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    PERFORMANCE_FX_PARAMS.forEach((p) => {
      init[p.id] = p.defaultValue;
    });
    return init;
  });
  const [fineTuneTarget, setFineTuneTarget] = useState<{ soundId: string; soundName: string; sectionLabel: string } | null>(null);
  const [showDrumPads, setShowDrumPads] = useState<boolean>(false);
  const [activeDrumPad, setActiveDrumPad] = useState<number | null>(null);

  // 8 Smart Chords Rack & Dynamic Live FX Visibility
  const [smartChords, setSmartChords] = useState<SmartChord[]>(() => loadSavedSmartChords());
  const [rootKey, setRootKey] = useState<ChordRootName>('D');
  const [isCustomChordModalOpen, setIsCustomChordModalOpen] = useState<boolean>(false);
  const [isLiveFxManualOpen, setIsLiveFxManualOpen] = useState<boolean>(false);
  const seqTimeoutsRef = useRef<Set<number>>(new Set());



  const requestLandscapeOrientation = async () => {
    try {
      if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
        await document.documentElement.requestFullscreen().catch(() => {});
      }
      if (screen.orientation && 'lock' in screen.orientation) {
        await (screen.orientation as any).lock('landscape').catch(() => {});
      }
    } catch {
      // Ignore
    }
  };

  // Modular 3-Bay Swap States & Deck Mode ('SLIDERS' vs 'SEQ_8_LINES')
  const [deckMode, setDeckMode] = useState<'SLIDERS' | 'SEQ_8_LINES'>('SLIDERS');
  const [leftBayShowSliders, setLeftBayShowSliders] = useState<boolean>(true);
  const [centerBayShowSliders, setCenterBayShowSliders] = useState<boolean>(true);
  const [activeEngineTab, setActiveEngineTab] = useState<'FM' | 'DRUMS' | 'VOCODER' | 'FX'>('FM');

  // Custom Slider Bank Assignments
  const [sliderBanks, setSliderBanks] = useState(STUDIO_DEFAULT_SLIDER_BANKS);
  const [editingBay, setEditingBay] = useState<'leftBay' | 'centerBay' | 'rightBay' | null>(null);

  // Parameter Values & DAW-Style Last-Touched MIDI Learn State
  const [paramValues, setParamValues] = useState<Record<string, number>>(() => ({
    ...getInitialParamValues(),
    ...synthEngine.getParameters(),
    ...expandPresetParamsToAllViews(loadPresetsWithUserSlots()[0]?.params),
  }));
  const [paramToCcMap, setParamToCcMap] = useState<Record<string, number>>(() => ({
    ...getInitialMidiCcMap(),
    ...loadParamToCcMappings(),
  }));
  const [lastTouchedParamId, setLastTouchedParamId] = useState<string>('cutoff');
  const [isGlobalMidiLearn, setIsGlobalMidiLearn] = useState<boolean>(false);
  const [learningParamId, setLearningParamId] = useState<string | null>(null);
  const [midiStatus, setMidiStatus] = useState<MidiDeviceStatus>({
    supported: true,
    connectedInputs: [],
    lastEventText: 'Scanning MIDI...',
    lastEventTimeMs: 0,
  });

  // Transport, Metronome, Sequencer Step Count (16 vs 21) & Mode States
  const [bpm, setBpm] = useState<number>(120.0);
  const [stepCountMode, setStepCountMode] = useState<16 | 21>(16);
  const [isMetronomeOn, setIsMetronomeOn] = useState<boolean>(false);
  const [scaleIdx, setScaleIdx] = useState<number>(0); // 0 = DORIAN
  const [isSeqModeActive, setIsSeqModeActive] = useState<boolean>(true);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const isLiveFxVisible = isPlaying || isRecording || isLiveFxManualOpen;
  const [isWarpStep, setIsWarpStep] = useState<boolean>(false);
  const [isHoldActive, setIsHoldActive] = useState<boolean>(false);
  const [isLowerLatch, setIsLowerLatch] = useState<boolean>(false);
  const [activeStepIdx, setActiveStepIdx] = useState<number>(0);

  // 8-Line Multi-Track Sequencer & Saved Sequence Bank State
  const [seqLines, setSeqLines] = useState<SequencerTrackLine[]>(() =>
    createInitial8SequenceLines('groove16')
  );
  const [selectedSeqLineIdx, setSelectedSeqLineIdx] = useState<number>(5); // Default selected: Line 6 (Chord Keys)
  const [sequenceBank, setSequenceBank] = useState<SavedSequenceSlot[]>(() =>
    loadSequenceBankFromStorage()
  );
  const [activeSeqBankSlot, setActiveSeqBankSlot] = useState<number>(1);
  const [seqSaveSlotTarget, setSeqSaveSlotTarget] = useState<number>(4);
  const [seqCustomTitle, setSeqCustomTitle] = useState<string>('My Custom Groove');
  const [isSaveSeqModalOpen, setIsSaveSeqModalOpen] = useState<boolean>(false);

  const [motionLocks, setMotionLocks] = useState<Partial<Record<SynthParamId, number>>[]>(() =>
    Array.from({ length: MAX_SEQ_STEPS }, () => ({}))
  );

  // Split Sound Presets for Lower Zone (Bass/Keys) and Upper Zone (Leads/Pads/Keys)
  const [lowerZonePreset, setLowerZonePreset] = useState<SynthPreset>(() => {
    const list = loadPresetsWithUserSlots();
    return (
      list.find((p) => p.id === 'bass-lately-solid-fm') ||
      list.find((p) => p.category === 'BASSES') ||
      list[0]
    );
  });
  const [upperZonePreset, setUpperZonePreset] = useState<SynthPreset>(() => {
    const list = loadPresetsWithUserSlots();
    return (
      list.find((p) => p.id === 'lead-cs80-ribbon-brass') ||
      list.find((p) => p.category === 'LEADS_BRASS') ||
      list[1]
    );
  });
  const [splitSoundPickerTarget, setSplitSoundPickerTarget] = useState<'ACTIVE' | 'LOWER' | 'UPPER'>('ACTIVE');
  const [isScaleSnapEnabled, setIsScaleSnapEnabled] = useState<boolean>(false);

  // Keybed Split & Independent Mini Octave States (Standard 24 Keys: C3 to B4)
  const [isSplitEnabled, setIsSplitEnabled] = useState<boolean>(true);
  const [splitMidiNote, setSplitMidiNote] = useState<number>(60); // C4 split boundary
  const [lowerZoneOctave, setLowerZoneOctave] = useState<number>(-1);
  const [upperZoneOctave, setUpperZoneOctave] = useState<number>(1);
  const [globalOctave, setGlobalOctave] = useState<number>(0);

  // Active Pressed Keys & MPE Touch Halos
  const [activeKeys, setActiveKeys] = useState<
    Record<number, { velocity: number; timbreY: number; pitchX: number }>
  >({});
  const [latchedLowerNote, setLatchedLowerNote] = useState<number | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  // Telemetry for VFD Visualizer & Peak Meters
  const [telemetry, setTelemetry] = useState<TelemetryFrame>({
    peakL: 0.1,
    peakR: 0.1,
    vocoderBands: new Array(16).fill(0.2),
    scope: new Array(128).fill(0),
  });

  const undoStackRef = useRef<SequencerTrackLine[][]>([]);
  const tapTimesRef = useRef<number[]>([]);
  const heldMidiNotesRef = useRef<Set<number>>(new Set());
  const heldMidiNoteMapRef = useRef<
    Map<number, { effectiveNote: number; zoneId: number; voiceTag: number }>
  >(new Map());

  // Refs for real-time sequencer & MIDI callbacks
  const seqLinesRef = useRef(seqLines);
  seqLinesRef.current = seqLines;
  const stepCountModeRef = useRef(stepCountMode);
  stepCountModeRef.current = stepCountMode;
  const motionLocksRef = useRef(motionLocks);
  motionLocksRef.current = motionLocks;
  const isMetronomeOnRef = useRef(isMetronomeOn);
  isMetronomeOnRef.current = isMetronomeOn;
  const isSeqModeActiveRef = useRef(isSeqModeActive);
  isSeqModeActiveRef.current = isSeqModeActive;
  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;
  const activeStepIdxRef = useRef(activeStepIdx);
  activeStepIdxRef.current = activeStepIdx;
  const selectedSeqLineIdxRef = useRef(selectedSeqLineIdx);
  selectedSeqLineIdxRef.current = selectedSeqLineIdx;
  const activePresetRef = useRef(activePreset);
  activePresetRef.current = activePreset;
  const presetsRef = useRef(presets);
  presetsRef.current = presets;
  const lowerZonePresetRef = useRef(lowerZonePreset);
  lowerZonePresetRef.current = lowerZonePreset;
  const upperZonePresetRef = useRef(upperZonePreset);
  upperZonePresetRef.current = upperZonePreset;
  const isSplitEnabledRef = useRef(isSplitEnabled);
  isSplitEnabledRef.current = isSplitEnabled;
  const keybedLayoutModeRef = useRef(keybedLayoutMode);
  keybedLayoutModeRef.current = keybedLayoutMode;
  const rootKeyRef = useRef(rootKey);
  rootKeyRef.current = rootKey;
  const scaleIdxRef = useRef(scaleIdx);
  scaleIdxRef.current = scaleIdx;
  const splitMidiNoteRef = useRef(splitMidiNote);
  splitMidiNoteRef.current = splitMidiNote;
  const lowerZoneOctaveRef = useRef(lowerZoneOctave);
  lowerZoneOctaveRef.current = lowerZoneOctave;
  const upperZoneOctaveRef = useRef(upperZoneOctave);
  upperZoneOctaveRef.current = upperZoneOctave;
  const globalOctaveRef = useRef(globalOctave);
  globalOctaveRef.current = globalOctave;
  const isScaleSnapEnabledRef = useRef(isScaleSnapEnabled);
  isScaleSnapEnabledRef.current = isScaleSnapEnabled;
  const bpmRef = useRef(bpm);
  bpmRef.current = bpm;
  const isWarpStepRef = useRef(isWarpStep);
  isWarpStepRef.current = isWarpStep;

  // DAW-Style Last-Touched MIDI Learn Toggle
  const toggleGlobalMidiLearn = () => {
    const nextMode = !isGlobalMidiLearn;
    setIsGlobalMidiLearn(nextMode);
    const armed = webMidiController.setGlobalMidiLearnMode(nextMode);
    setLearningParamId(armed);
    if (nextMode && armed) {
      showToast(
        `MIDI LEARN ON: Last used slider [${getParamLabel(armed)}] is armed! Move any MIDI knob (or touch any other slider first).`
      );
    } else {
      showToast('MIDI Learn Mode OFF');
    }
  };

  const toggleMidiLearnForParam = (paramId: string) => {
    setLastTouchedParamId(paramId);
    webMidiController.notifyParameterTouched(paramId);
    const next = learningParamId === paramId ? null : paramId;
    setLearningParamId(next);
    webMidiController.setLearningParam(next);
    if (!next) {
      setIsGlobalMidiLearn(false);
    } else {
      showToast(
        `MIDI LEARN: Move any physical knob/slider on your MIDI controller to map [${getParamLabel(next)}]`
      );
    }
  };

  // Orientation & Mobile Fullscreen Management
  useEffect(() => {
    const updateOrientationAndFullscreen = () => {
      const portrait = window.innerHeight > window.innerWidth && window.innerWidth < 850;
      setIsPortrait(portrait);
      setIsCompactViewport(window.innerHeight < 520 || window.innerWidth < 920);
      const fs = Boolean(
        document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement
      );
      setIsFullscreen(fs);
    };

    const handleGlobalRelease = () => {
      setActiveKeys((prev) => {
        const keys = Object.keys(prev).map(Number);
        if (keys.length > 0) {
          keys.forEach((note) => {
            synthEngine.noteOff(note, 2000 + note, 1);
            synthEngine.noteOff(note, 100 + note, 0);
          });
          return {};
        }
        return prev;
      });
    };

    window.addEventListener('resize', updateOrientationAndFullscreen);
    window.addEventListener('orientationchange', updateOrientationAndFullscreen);
    document.addEventListener('fullscreenchange', updateOrientationAndFullscreen);
    document.addEventListener('webkitfullscreenchange', updateOrientationAndFullscreen);
    window.addEventListener('pointercancel', handleGlobalRelease);
    window.addEventListener('blur', handleGlobalRelease);
    document.addEventListener('visibilitychange', handleGlobalRelease);

    updateOrientationAndFullscreen();

    return () => {
      window.removeEventListener('resize', updateOrientationAndFullscreen);
      window.removeEventListener('orientationchange', updateOrientationAndFullscreen);
      document.removeEventListener('fullscreenchange', updateOrientationAndFullscreen);
      document.removeEventListener('webkitfullscreenchange', updateOrientationAndFullscreen);
      window.removeEventListener('pointercancel', handleGlobalRelease);
      window.removeEventListener('blur', handleGlobalRelease);
      document.removeEventListener('visibilitychange', handleGlobalRelease);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      const doc = document as any;
      const el = document.documentElement as any;
      const isFs = Boolean(
        doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement
      );
      if (!isFs) {
        const rfs =
          el.requestFullscreen ||
          el.webkitRequestFullscreen ||
          el.mozRequestFullScreen ||
          el.msRequestFullscreen;
        if (rfs) {
          await rfs.call(el);
          setIsFullscreen(true);
        }
        try {
          if (screen.orientation && (screen.orientation as any).lock) {
            await (screen.orientation as any).lock('landscape').catch(() => {});
          }
        } catch {}
      } else {
        const efs =
          doc.exitFullscreen ||
          doc.webkitExitFullscreen ||
          doc.mozCancelFullScreen ||
          doc.msExitFullscreen;
        if (efs) {
          await efs.call(doc);
          setIsFullscreen(false);
        }
      }
    } catch (err) {
      console.warn('Fullscreen toggle failed:', err);
    }
  };

  // Load saved workspace layout & initialize Web MIDI Hardware Controller on mount
  useEffect(() => {
    let initialLower = lowerZonePresetRef.current;
    let initialUpper = upperZonePresetRef.current;

    const saved = loadWorkspaceLayoutFromStorage();
    if (saved) {
      setLeftBayShowSliders(saved.leftBayShowSliders);
      setCenterBayShowSliders(saved.centerBayShowSliders);
      setIsSplitEnabled(saved.isSplitEnabled);
      setLowerZoneOctave(saved.lowerZoneOctave);
      setUpperZoneOctave(saved.upperZoneOctave);
      setGlobalOctave(saved.globalOctave ?? 0);
      if (saved.splitMidiNote) {
        setSplitMidiNote(saved.splitMidiNote);
      }
      if (saved.lowerZonePresetId) {
        const found = loadPresetsWithUserSlots().find((p) => p.id === saved.lowerZonePresetId);
        if (found) {
          setLowerZonePreset(found);
          lowerZonePresetRef.current = found;
          initialLower = found;
        }
      }
      if (saved.upperZonePresetId) {
        const found = loadPresetsWithUserSlots().find((p) => p.id === saved.upperZonePresetId);
        if (found) {
          setUpperZonePreset(found);
          upperZonePresetRef.current = found;
          initialUpper = found;
        }
      }
      if (saved.sliderBanks) {
        setSliderBanks(saved.sliderBanks);
      }
    }

    // Sync initial split zone presets to the AudioWorklet DSP processor
    void synthEngine.setZonePreset(0, initialLower);
    void synthEngine.setZonePreset(1, initialUpper);

    void webMidiController.initialize({
      onNoteOn: (midiNote, velocityNorm) => {
        // Prevent any duplicate Note On for an already-held MIDI key
        if (heldMidiNotesRef.current.has(midiNote)) {
          return;
        }
        heldMidiNotesRef.current.add(midiNote);

        // Match the exact split & preset routing of the on-screen keybed!
        const useSplit =
          keybedLayoutModeRef.current === 'FULL_24_KEYS' && isSplitEnabledRef.current;
        const isLower = useSplit && midiNote < splitMidiNoteRef.current;
        const octOffset = useSplit
          ? isLower
            ? lowerZoneOctaveRef.current * 12
            : upperZoneOctaveRef.current * 12
          : globalOctaveRef.current * 12;

        const naturalNote = Math.max(12, Math.min(108, midiNote + octOffset));
        const rootMidiOffset = ROOT_SEMITONES[rootKeyRef.current] ?? 2;
        const activeScale = MUSICAL_SCALES[scaleIdxRef.current] || MUSICAL_SCALES[0];
        const effectiveNote = isScaleSnapEnabledRef.current
          ? quantizeNoteToScale(naturalNote, rootMidiOffset, activeScale)
          : naturalNote;

        const preset = useSplit
          ? isLower
            ? lowerZonePresetRef.current
            : upperZonePresetRef.current
          : activePresetRef.current;

        const zoneId = isLower ? 0 : 1;
        const voiceTag = 500 + midiNote;
        heldMidiNoteMapRef.current.set(midiNote, { effectiveNote, zoneId, voiceTag });

        void synthEngine.noteOn(
          effectiveNote,
          velocityNorm,
          voiceTag,
          zoneId,
          preset.targetEngineId,
          preset.opRatios,
          preset.opLevels,
          preset.params
        );
        setActiveKeys((prev) => ({
          ...prev,
          [midiNote]: { velocity: velocityNorm, timbreY: velocityNorm, pitchX: 0 },
        }));

        // Support single note OR full polyphonic chord when multiple MIDI keys are held simultaneously!
        const currentlyHeldNotes = Array.from(heldMidiNotesRef.current).sort((a, b) => a - b);
        const isMultiKeyChord = currentlyHeldNotes.length >= 2;
        const chordNotes = isMultiKeyChord
          ? currentlyHeldNotes.map((n) => Math.max(12, Math.min(108, n + octOffset)))
          : [effectiveNote];
        const chordTag = isMultiKeyChord
          ? `${formatMidiNote(chordNotes[0])}+${chordNotes.length}`
          : undefined;

        setSeqLines((prev) =>
          prev.map((line, lIdx) => {
            if (lIdx !== selectedSeqLineIdxRef.current || line.kind !== 'synth') return line;
            if (isRecordingRef.current && isSeqModeActiveRef.current) {
              const stepIdx = activeStepIdxRef.current;
              return {
                ...line,
                defaultNotes: chordNotes,
                defaultChordLabel: chordTag,
                steps: line.steps.map((st, sIdx) =>
                  sIdx === stepIdx
                    ? { active: true, notes: chordNotes, chordLabel: chordTag, velocity: velocityNorm }
                    : st
                ),
              };
            }
            return { ...line, defaultNotes: chordNotes, defaultChordLabel: chordTag };
          })
        );
      },
      onNoteOff: (midiNote) => {
        heldMidiNotesRef.current.delete(midiNote);
        const playedInfo = heldMidiNoteMapRef.current.get(midiNote);
        if (playedInfo) {
          heldMidiNoteMapRef.current.delete(midiNote);
          synthEngine.noteOff(playedInfo.effectiveNote, playedInfo.voiceTag, playedInfo.zoneId);
        } else {
          synthEngine.noteOff(midiNote, 500 + midiNote);
        }
        setActiveKeys((prev) => {
          if (!prev[midiNote]) return prev;
          const next = { ...prev };
          delete next[midiNote];
          return next;
        });
      },
      onDrumPadNote: (drumIdx, velocityNorm) => {
        void synthEngine.triggerDrum(drumIdx, velocityNorm);
        setActiveDrumPad(drumIdx);
        window.setTimeout(() => setActiveDrumPad((curr) => (curr === drumIdx ? null : curr)), 120);
        if (isRecordingRef.current && isSeqModeActiveRef.current) {
          const stepIdx = activeStepIdxRef.current;
          const targetLine = drumIdx < 4 ? drumIdx : 3;
          setSeqLines((prev) =>
            prev.map((line, lIdx) =>
              lIdx === targetLine
                ? {
                    ...line,
                    kind: 'drum',
                    drumIdx,
                    steps: line.steps.map((st, sIdx) =>
                      sIdx === stepIdx ? { ...st, active: true, velocity: velocityNorm } : st
                    ),
                  }
                : line
            )
          );
        }
      },
      onParamCcChange: (paramId, normalizedValue) => {
        setParamValues((prev) => ({
          ...prev,
          ...getSynchronizedParamUpdates(paramId, normalizedValue, prev),
        }));
        synthEngine.setParameter(paramId, normalizedValue);
        if (isRecordingRef.current && isSeqModeActiveRef.current) {
          const stepIdx = activeStepIdxRef.current;
          setMotionLocks((prev) =>
            prev.map((locks, idx) =>
              idx === stepIdx ? { ...locks, [paramId]: normalizedValue } : locks
            )
          );
        }
      },
      onPitchBend: (semitones) => {
        synthEngine.sendGlobalPitchBend(semitones);
      },
      onSustainPedal: (held) => {
        setIsHoldActive(held);
        synthEngine.setHoldPedal(held);
      },
      onTransportStartStop: (playing) => {
        setIsPlaying(playing);
      },
      onCcLearned: (paramId, ccNumber) => {
        if (!webMidiController.isGlobalMidiLearnMode()) {
          setLearningParamId(null);
        }
        setParamToCcMap(webMidiController.getParamToCcMap());
        showToast(`✓ Mapped MIDI CC ${ccNumber} -> ${getParamLabel(paramId)}`);
      },
      onStatusChange: (status) => {
        setMidiStatus({ ...status });
      },
    });
  }, []);

  const stopSequencerPlayback = () => {
    seqTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
    seqTimeoutsRef.current.clear();
    synthEngine.panicAllNotesOff();
  };

  // Trigger a single line's sound (supports single note OR full polyphonic chord in a single step space!)
  const triggerLineSound = (
    line: SequencerTrackLine,
    notesOverride?: number[],
    durationMs = 190
  ) => {
    if (line.kind === 'drum') {
      void synthEngine.triggerDrum(line.drumIdx, 0.92);
    } else {
      const presetObj =
        presetsRef.current.find((p) => p.id === line.presetId) || FACTORY_PRESETS[0];
      const playNotes =
        notesOverride && notesOverride.length > 0 ? notesOverride : line.defaultNotes;
      playNotes.forEach((n, idx) => {
        const voiceTag = 900 + line.id * 25 + idx * 4 + (n % 12);
        void synthEngine.noteOn(
          n,
          playNotes.length > 1 ? 0.72 : 0.84,
          voiceTag,
          1,
          presetObj.targetEngineId,
          presetObj.opRatios,
          presetObj.opLevels,
          presetObj.params
        );
        const tid = window.setTimeout(() => {
          synthEngine.noteOff(n, voiceTag, 1);
          seqTimeoutsRef.current.delete(tid);
        }, durationMs);
        seqTimeoutsRef.current.add(tid);
      });
    }
  };

  // BPM-Synchronized 8-Line Sequencer & Metronome Clock Loop (Supports 16 or 21 Slots!)
  // Uses drift-compensated recursive setTimeout so tempo & metronome adjustments NEVER tear down timers or stutter
  useEffect(() => {
    if (!isPlaying) return;

    let isMounted = true;
    let timerId: number | null = null;
    let expectedTime = performance.now();

    const scheduleNextTick = () => {
      if (!isMounted) return;

      const currentBpm = bpmRef.current;
      const totalSlots = stepCountModeRef.current;
      const stepMs = isWarpStepRef.current
        ? calculateWarpStepDurationMs(currentBpm, totalSlots, totalSlots)
        : 60000 / currentBpm / 4;

      expectedTime += stepMs;
      const now = performance.now();
      const delay = Math.max(0, expectedTime - now);

      timerId = window.setTimeout(() => {
        if (!isMounted) return;

        setActiveStepIdx((prev) => {
          const limit = stepCountModeRef.current;
          const next = (prev + 1) % limit;

          // 1. Trigger Studio Metronome on quarter-note beats when Metronome is ON
          if (isMetronomeOnRef.current && next % 4 === 0) {
            void synthEngine.triggerMetronome(next === 0, next === 0 ? 0.9 : 0.72);
          }

          // 2. Trigger all active unmuted lines among the 8 Sequence Lines when SEQ MODE is active
          if (isSeqModeActiveRef.current) {
            const stepLocks = motionLocksRef.current[next];
            if (stepLocks) {
              Object.entries(stepLocks).forEach(([k, val]) => {
                if (val !== undefined) {
                  synthEngine.setParameter(k as any, val);
                  setParamValues((curr) => ({ ...curr, [k]: val }));
                }
              });
            }

            const currentLines = seqLinesRef.current;
            const anySoloed = currentLines.some((l) => l.soloed);
            const liveStepMs = isWarpStepRef.current
              ? calculateWarpStepDurationMs(bpmRef.current, limit, limit)
              : 60000 / bpmRef.current / 4;

            for (let lIdx = 0; lIdx < currentLines.length; lIdx++) {
              const line = currentLines[lIdx];
              if (anySoloed ? !line.soloed || line.muted : line.muted) continue;
              const cell = line.steps[next];
              if (cell && cell.active) {
                const gateMultiplier = line.gateSteps || 1;
                const duration = Math.max(75, liveStepMs * gateMultiplier * 0.92);
                triggerLineSound(line, cell.notes, duration);
              }
            }
          }

          return next;
        });

        scheduleNextTick();
      }, delay);
    };

    const firstStepMs = isWarpStepRef.current
      ? calculateWarpStepDurationMs(bpmRef.current, stepCountModeRef.current, stepCountModeRef.current)
      : 60000 / bpmRef.current / 4;
    expectedTime = performance.now();
    timerId = window.setTimeout(scheduleNextTick, firstStepMs);

    return () => {
      isMounted = false;
      if (timerId !== null) window.clearTimeout(timerId);
      stopSequencerPlayback();
    };
  }, [isPlaying]);

  const showToast = (msg: string) => {
    setToastMsg(msg);
    window.setTimeout(() => setToastMsg((curr) => (curr === msg ? null : curr)), 2400);
  };

  const pushUndoSnapshot = () => {
    const deepCopy = seqLines.map((line) => ({
      ...line,
      defaultNotes: [...line.defaultNotes],
      steps: line.steps.map((s) => ({ ...s, notes: [...s.notes] })),
    }));
    undoStackRef.current.push(deepCopy);
    if (undoStackRef.current.length > 16) {
      undoStackRef.current.shift();
    }
  };

  const handleUndo = () => {
    const last = undoStackRef.current.pop();
    if (last) {
      setSeqLines(last);
      showToast('Reverted last 8-Line Sequence edit');
    } else {
      synthEngine.panicAllNotesOff();
      setActiveKeys({});
      setLatchedLowerNote(null);
      showToast('All Notes Off (Audio Panic)');
    }
  };

  const handleSaveLayout = () => {
    saveWorkspaceLayoutToStorage({
      version: '1.0.0',
      savedAtUtc: new Date().toISOString(),
      leftBayShowSliders,
      centerBayShowSliders,
      isSplitEnabled,
      splitMidiNote,
      lowerZoneOctave,
      upperZoneOctave,
      globalOctave,
      lowerZonePresetId: lowerZonePreset.id,
      upperZonePresetId: upperZonePreset.id,
      sliderBanks,
    });
    showToast('Workspace Layout & Split Settings Saved');
  };

  // Save Current 8-Line Sequence into the Sequence Bank below the Sequencer
  const handleSaveSequenceToBank = (slotNum: number, overrideTitle?: string) => {
    const rawTitle = (overrideTitle !== undefined ? overrideTitle : seqCustomTitle).trim();
    const cleanName = rawTitle.replace(/^\d+:\s*/, '') || `Seq ${slotNum} (${stepCountMode} Steps)`;
    setSeqCustomTitle(cleanName);

    const deepLines = seqLines.map((line) => ({
      ...line,
      defaultNotes: [...line.defaultNotes],
      steps: line.steps.map((s) => ({ ...s, notes: [...s.notes] })),
    }));

    const nextBank = sequenceBank.map((slot) =>
      slot.slotIndex === slotNum
        ? {
            slotIndex: slotNum,
            name: `${slotNum}: ${cleanName}`,
            stepCount: stepCountMode,
            bpm,
            isPopulated: true,
            lines: deepLines,
          }
        : slot
    );

    setSequenceBank(nextBank);
    saveSequenceBankToStorage(nextBank);
    setActiveSeqBankSlot(slotNum);
    setSeqSaveSlotTarget(slotNum);
    showToast(`✓ Saved Sequence "${cleanName}" to Sequence Bank Slot ${slotNum}!`);
  };

  const handleRenameSequenceSlot = (slotIndex: number, newTitle: string) => {
    const cleanName = newTitle.trim().replace(/^\d+:\s*/, '');
    const nextBank = sequenceBank.map((slot) =>
      slot.slotIndex === slotIndex
        ? {
            ...slot,
            name: `${slotIndex}: ${cleanName || `Slot ${slotIndex}`}`,
          }
        : slot
    );
    setSequenceBank(nextBank);
    saveSequenceBankToStorage(nextBank);
    if (activeSeqBankSlot === slotIndex) {
      setSeqCustomTitle(cleanName);
    }
    showToast(`✓ Renamed Sequence Slot #${slotIndex} to "${cleanName}"`);
  };

  const handleClearSequenceSlot = (slotIndex: number) => {
    const nextBank = sequenceBank.map((slot) =>
      slot.slotIndex === slotIndex
        ? {
            slotIndex,
            name: `${slotIndex}: [Empty Slot]`,
            stepCount: 16 as const,
            bpm: 120.0,
            isPopulated: false,
            lines: createInitial8SequenceLines('groove16').map((l) => ({
              ...l,
              steps: l.steps.map((s) => ({ ...s, active: false })),
            })),
          }
        : slot
    );
    setSequenceBank(nextBank);
    saveSequenceBankToStorage(nextBank);
    showToast(`Cleared Sequence Slot #${slotIndex}`);
  };

  // Bus Master Group Queries & Handlers
  const isDrumBusAllMuted = seqLines
    .filter((l) => (l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'DRUMS')
    .every((l) => l.muted);

  const isDrumBusAllSoloed = seqLines
    .filter((l) => (l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'DRUMS')
    .every((l) => l.soloed);

  const isSynthBusAllMuted = seqLines
    .filter((l) => (l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'SYNTHS')
    .every((l) => l.muted);

  const isSynthBusAllSoloed = seqLines
    .filter((l) => (l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'SYNTHS')
    .every((l) => l.soloed);

  const handleToggleBusMute = (targetBus: 'DRUMS' | 'SYNTHS') => {
    pushUndoSnapshot();
    const isMutedNow = targetBus === 'DRUMS' ? isDrumBusAllMuted : isSynthBusAllMuted;
    setSeqLines((prev) =>
      prev.map((l) => {
        const lineBus = l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS');
        return lineBus === targetBus ? { ...l, muted: !isMutedNow } : l;
      })
    );
    showToast(`${targetBus === 'DRUMS' ? '🥁 DRUM BUS' : '🎹 SYNTH BUS'} ${!isMutedNow ? 'MUTED' : 'UNMUTED'}`);
  };

  const handleToggleBusSolo = (targetBus: 'DRUMS' | 'SYNTHS') => {
    pushUndoSnapshot();
    const isSoloedNow = targetBus === 'DRUMS' ? isDrumBusAllSoloed : isSynthBusAllSoloed;
    setSeqLines((prev) =>
      prev.map((l) => {
        const lineBus = l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS');
        if (isSoloedNow) {
          return lineBus === targetBus ? { ...l, soloed: false } : l;
        } else {
          return { ...l, soloed: lineBus === targetBus };
        }
      })
    );
    showToast(`${targetBus === 'DRUMS' ? '🥁 DRUM BUS' : '🎹 SYNTH BUS'} SOLO ${!isSoloedNow ? 'ON' : 'OFF'}`);
  };

  const handleClearBusSteps = (targetBus: 'DRUMS' | 'SYNTHS') => {
    pushUndoSnapshot();
    setSeqLines((prev) =>
      prev.map((l) => {
        const lineBus = l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS');
        return lineBus === targetBus
          ? { ...l, steps: l.steps.map((s) => ({ ...s, active: false })) }
          : l;
      })
    );
    showToast(`Cleared pattern steps on ${targetBus === 'DRUMS' ? '🥁 DRUM BUS' : '🎹 SYNTH BUS'}`);
  };

  // Load a Saved Sequence from the Sequence Bank Bar below the Sequencer
  const handleLoadSequenceFromBank = (slot: SavedSequenceSlot) => {
    pushUndoSnapshot();
    setActiveSeqBankSlot(slot.slotIndex);
    setSeqSaveSlotTarget(slot.slotIndex);
    setStepCountMode(slot.stepCount);
    setBpm(slot.bpm);
    const cloned = slot.lines.map((line) => ({
      ...line,
      defaultNotes: [...line.defaultNotes],
      steps: line.steps.map((s) => ({ ...s, notes: [...s.notes] })),
    }));
    setSeqLines(cloned);
    setSeqCustomTitle(slot.name.replace(/^\d+:\s*/, ''));
    showToast(`Loaded Sequence Bank #${slot.slotIndex}: ${slot.name} (${slot.stepCount} Slots)`);
  };

  const handleTapTempo = () => {
    const now = performance.now();
    const recent = tapTimesRef.current.filter((t) => now - t < 2500);
    recent.push(now);
    tapTimesRef.current = recent;
    if (recent.length >= 3) {
      let sumDiff = 0;
      for (let i = 1; i < recent.length; i++) {
        sumDiff += recent[i] - recent[i - 1];
      }
      const avgMs = sumDiff / (recent.length - 1);
      const calculatedBpm = Math.max(40, Math.min(240, Number((60000 / avgMs).toFixed(1))));
      setBpm(calculatedBpm);
      showToast(`Tempo Set: ${calculatedBpm.toFixed(1)} BPM`);
    }
  };

  // Every slider interaction updates lastTouchedParamId so DAW-style MIDI Learn always maps the last slider used!
  const handleParamChange = (paramId: string, nextVal: number) => {
    setLastTouchedParamId(paramId);
    webMidiController.notifyParameterTouched(paramId);
    if (isGlobalMidiLearn) {
      setLearningParamId(paramId);
    }

    setParamValues((prev) => ({
      ...prev,
      ...getSynchronizedParamUpdates(paramId, nextVal, prev),
    }));
    synthEngine.setParameter(paramId as any, nextVal);

    if (isRecording && isSeqModeActive) {
      setMotionLocks((prev) =>
        prev.map((locks, idx) => (idx === activeStepIdx ? { ...locks, [paramId]: nextVal } : locks))
      );
    }
  };

  const handlePerfFxChange = (paramId: string, nextVal: number) => {
    setPerformanceFxValues((prev) => ({ ...prev, [paramId]: nextVal }));
    synthEngine.setParameter(paramId as any, nextVal);
    webMidiController.notifyParameterTouched(paramId);
  };

  const handleRootKeyChange = (newKey: ChordRootName) => {
    const prevSemis = ROOT_SEMITONES[rootKey] ?? 2;
    const nextSemis = ROOT_SEMITONES[newKey] ?? 2;
    const delta = nextSemis - prevSemis;
    setRootKey(newKey);

    setSmartChords((prevList) => {
      const transposed = prevList.map((ch, idx) => {
        const curRootSemis = ROOT_SEMITONES[ch.root as ChordRootName] ?? 0;
        const newChordRoot = CHORD_ROOTS[(((curRootSemis + delta) % 12) + 12) % 12];
        return buildSmartChord(idx, newChordRoot, ch.type, ch.voicing);
      });
      saveSmartChordsToStorage(transposed);
      return transposed;
    });

    showToast(`✓ Key Transposed to ${newKey} (Scale & 8 Chords Transposed)`);
  };

  const currentScale = MUSICAL_SCALES[scaleIdx];

  // Keybed Note Span: 24 Keys (Standard 2 Octaves: C3 [48] to B4 [71])
  const keybedBaseNotes = Array.from({ length: 24 }, (_, idx) => 48 + idx);
  const isBlackKey = (midi: number) => [1, 3, 6, 8, 10].includes(midi % 12);

  const playOctavePreviewBlip = (baseMidi: number, octOffset: number, preset: SynthPreset) => {
    const previewNote = Math.max(24, Math.min(96, baseMidi + octOffset * 12));
    void synthEngine.noteOn(
      previewNote,
      0.65,
      9999,
      1,
      preset.targetEngineId,
      preset.opRatios,
      preset.opLevels,
      preset.params
    );
    window.setTimeout(() => {
      synthEngine.noteOff(previewNote, 9999, 1);
    }, 110);
  };

  const handleLowerOctaveChange = (delta: number) => {
    const rootMidiOffset = ROOT_SEMITONES[rootKey] ?? 2;
    setLowerZoneOctave((prev) => {
      const next = Math.max(-3, Math.min(3, prev + delta));
      if (latchedLowerNote !== null) {
        const oldNatural = latchedLowerNote + prev * 12;
        const newNatural = latchedLowerNote + next * 12;
        const oldFinal = isScaleSnapEnabled ? quantizeNoteToScale(oldNatural, rootMidiOffset, currentScale) : oldNatural;
        const newFinal = isScaleSnapEnabled ? quantizeNoteToScale(newNatural, rootMidiOffset, currentScale) : newNatural;
        const latchedTag = 100 + latchedLowerNote;
        synthEngine.noteOff(oldFinal, latchedTag, 0);
        synthEngine.noteOn(
          newFinal,
          0.85,
          latchedTag,
          0,
          lowerZonePreset.targetEngineId,
          lowerZonePreset.opRatios,
          lowerZonePreset.opLevels,
          lowerZonePreset.params
        );
      }
      playOctavePreviewBlip(48, next, lowerZonePreset);
      showToast(`Lower Split Zone: OCT ${next >= 0 ? `+${next}` : next}`);
      return next;
    });
  };

  const handleUpperOctaveChange = (delta: number) => {
    setUpperZoneOctave((prev) => {
      const next = Math.max(-3, Math.min(3, prev + delta));
      playOctavePreviewBlip(60, next, upperZonePreset);
      showToast(`Upper Split Zone: OCT ${next >= 0 ? `+${next}` : next}`);
      return next;
    });
  };

  const handleGlobalOctaveChange = (delta: number) => {
    setGlobalOctave((prev) => {
      const next = Math.max(-3, Math.min(3, prev + delta));
      playOctavePreviewBlip(48, next, activePreset);
      showToast(`Global Keyboard: OCT ${next >= 0 ? `+${next}` : next}`);
      return next;
    });
  };

  const handleKeyPointerDown = (
    e: React.PointerEvent<HTMLElement>,
    rawNote: number,
    isLowerZone: boolean
  ) => {
    e.stopPropagation();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Ignore
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const relY = Math.max(0, Math.min(1, (e.clientY - rect.top) / Math.max(1, rect.height)));
    const velocity = Math.max(0.4, Math.min(1.0, 0.4 + 0.6 * Math.pow(relY, 1.1)));
    const timbreY = 1.0 - relY;

    const useSplit = isSplitEnabled;
    const octOffset = useSplit
      ? isLowerZone
        ? lowerZoneOctave * 12
        : upperZoneOctave * 12
      : globalOctave * 12;

    const rootMidiOffset = ROOT_SEMITONES[rootKey] ?? 2;
    const naturalNote = rawNote + octOffset;
    const finalNote = isScaleSnapEnabled
      ? quantizeNoteToScale(naturalNote, rootMidiOffset, currentScale)
      : naturalNote;

    const voiceTag = (useSplit && isLowerZone ? 100 : 2000) + rawNote;

    if (useSplit && isLowerZone && isLowerLatch) {
      if (latchedLowerNote !== null && latchedLowerNote !== rawNote) {
        const oldNatural = latchedLowerNote + octOffset;
        const oldFinal = isScaleSnapEnabled
          ? quantizeNoteToScale(oldNatural, rootMidiOffset, currentScale)
          : oldNatural;
        const oldTag = 100 + latchedLowerNote;
        synthEngine.noteOff(oldFinal, oldTag, 0);
      }
      setLatchedLowerNote(rawNote);
    }

    setActiveKeys((prev) => ({
      ...prev,
      [rawNote]: { velocity, timbreY, pitchX: 0 },
    }));

    const activeZonePreset = useSplit
      ? isLowerZone
        ? lowerZonePreset
        : upperZonePreset
      : activePreset;

    void synthEngine.noteOn(
      finalNote,
      velocity,
      voiceTag,
      useSplit && isLowerZone ? 0 : 1,
      activeZonePreset.targetEngineId,
      activeZonePreset.opRatios,
      activeZonePreset.opLevels,
      activeZonePreset.params
    );

    // Set the selected synth sequence line's brush to this single note (and record if [● REC] is active)
    setSeqLines((prev) =>
      prev.map((line, lIdx) => {
        if (lIdx !== selectedSeqLineIdx || line.kind !== 'synth') return line;
        if (isRecording && isSeqModeActive) {
          const nextSteps = line.steps.map((st, sIdx) =>
            sIdx === activeStepIdx
              ? { active: true, notes: [finalNote], chordLabel: undefined, velocity }
              : st
          );
          return {
            ...line,
            defaultNotes: [finalNote],
            defaultChordLabel: undefined,
            steps: nextSteps,
          };
        }
        return { ...line, defaultNotes: [finalNote], defaultChordLabel: undefined };
      })
    );
  };

  const handleKeyPointerMove = (
    e: React.PointerEvent<HTMLDivElement>,
    rawNote: number,
    isLowerZone = false
  ) => {
    if (!activeKeys[rawNote]) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const relY = Math.max(0, Math.min(1, (e.clientY - rect.top) / Math.max(1, rect.height)));
    const relX = (e.clientX - (rect.left + rect.width * 0.5)) / Math.max(1, rect.width);
    const timbreY = 1.0 - relY;
    // Apply a center deadzone so normal finger micro-jitter never detunes the note!
    const deadzoneX = Math.abs(relX) < 0.25 ? 0 : (relX - Math.sign(relX) * 0.25) / 0.75;
    const pitchDeltaSemis = Math.max(-1.0, Math.min(1.0, deadzoneX * 1.0));

    const useSplit = keybedLayoutMode === 'FULL_24_KEYS' && isSplitEnabled;
    const voiceTag = (useSplit && isLowerZone ? 100 : 2000) + rawNote;
    synthEngine.sendMpeExpression(voiceTag, pitchDeltaSemis, timbreY);
  };

  const handleKeyPointerUp = (rawNote: number, isLowerZone: boolean) => {
    const useSplit = isSplitEnabled;
    const octOffset = useSplit
      ? isLowerZone
        ? lowerZoneOctave * 12
        : upperZoneOctave * 12
      : globalOctave * 12;
    const naturalNote = rawNote + octOffset;
    const rootMidiOffset = ROOT_SEMITONES[rootKey] ?? 2;
    const finalNote = isScaleSnapEnabled
      ? quantizeNoteToScale(naturalNote, rootMidiOffset, currentScale)
      : naturalNote;

    if (useSplit && isLowerZone && isLowerLatch) {
      return;
    }

    setActiveKeys((prev) => {
      if (!prev[rawNote]) return prev;
      const next = { ...prev };
      delete next[rawNote];
      return next;
    });

    const voiceTag = (useSplit && isLowerZone ? 100 : 2000) + rawNote;
    synthEngine.noteOff(finalNote, voiceTag, useSplit && isLowerZone ? 0 : 1);
  };

  // Pressing a Smart Chord plays the full chord AND arms that full chord as the brush for single-step placement in the Sequencer!
  const handleChordTriggerDown = (chordLabel: string, offsets: number[]) => {
    const rootMidi = 60 + (ROOT_SEMITONES[rootKey] ?? 2);
    const chordNotes = offsets.map((off) => rootMidi + off);
    chordNotes.forEach((n, idx) => {
      void synthEngine.noteOn(
        n,
        0.8,
        700 + idx,
        1,
        activePreset.targetEngineId,
        activePreset.opRatios,
        activePreset.opLevels,
        activePreset.params
      );
    });

    // Set the selected synth line's brush to this FULL CHORD so tapping any single step places the entire chord!
    setSeqLines((prev) =>
      prev.map((line, lIdx) => {
        if (lIdx !== selectedSeqLineIdx || line.kind !== 'synth') return line;
        if (isRecording && isSeqModeActive) {
          pushUndoSnapshot();
          return {
            ...line,
            defaultNotes: chordNotes,
            defaultChordLabel: chordLabel,
            steps: line.steps.map((st, sIdx) =>
              sIdx === activeStepIdx
                ? { active: true, notes: chordNotes, chordLabel, velocity: 0.84 }
                : st
            ),
          };
        }
        return {
          ...line,
          defaultNotes: chordNotes,
          defaultChordLabel: chordLabel,
        };
      })
    );
  };

  const handleChordTriggerUp = (offsets: number[]) => {
    const rootMidi = 60 + (ROOT_SEMITONES[rootKey] ?? 2);
    offsets.forEach((off, idx) => {
      synthEngine.noteOff(rootMidi + off, 700 + idx, 1);
    });
  };

  const handleAuditionSmartChord = (chord: SmartChord) => {
    handleChordTriggerDown(chord.label, chord.offsets);
    setTimeout(() => {
      handleChordTriggerUp(chord.offsets);
    }, 480);
  };

  const handleSaveSmartChords = (newChords: SmartChord[]) => {
    setSmartChords(newChords);
    saveSmartChordsToStorage(newChords);
    showToast('Applied & Saved 8 Smart Chords');
  };

  const beatPulseDurationSec = (60 / Math.max(40, Math.min(240, bpm))).toFixed(2);
  const currentBeatNumber = Math.floor(activeStepIdx / 4) + 1;

  return (
    <div
      style={{
        width: '100vw',
        height: '100dvh',
        maxHeight: '100dvh',
        background: PocketGridRetroPalette.chassisGunmetal,
        color: PocketGridRetroPalette.warmIvoryText,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        boxSizing: 'border-box',
        borderLeft: isPortrait ? 'none' : `4px solid ${PocketGridRetroPalette.warmWalnut}`,
        borderRight: isPortrait ? 'none' : `4px solid ${PocketGridRetroPalette.warmWalnut}`,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Keyframe Animation for BPM-Synchronized Pulsing Sequencer Box & Rotate Device Phone Icon */}
      <style>{`
        @keyframes pocketGridSeqPulse {
          0% {
            box-shadow: 0 0 4px rgba(217, 148, 59, 0.35), inset 0 0 4px rgba(217, 148, 59, 0.15);
            border-color: rgba(217, 148, 59, 0.55);
          }
          50% {
            box-shadow: 0 0 16px rgba(217, 148, 59, 0.92), inset 0 0 10px rgba(217, 148, 59, 0.35);
            border-color: #FFF3D6;
          }
          100% {
            box-shadow: 0 0 4px rgba(217, 148, 59, 0.35), inset 0 0 4px rgba(217, 148, 59, 0.15);
            border-color: rgba(217, 148, 59, 0.55);
          }
        }
        @keyframes pocketGridRecPulse {
          0% { box-shadow: 0 0 4px rgba(200, 62, 55, 0.45); }
          50% { box-shadow: 0 0 16px rgba(200, 62, 55, 0.98); }
          100% { box-shadow: 0 0 4px rgba(200, 62, 55, 0.45); }
        }
        @keyframes pocketGridRotateDevice {
          0% { transform: rotate(0deg); }
          30% { transform: rotate(-90deg); }
          70% { transform: rotate(-90deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>

      {/* =====================================================================
          FORCE LANDSCAPE MODE OVERLAY FOR MOBILE PHONES
         ===================================================================== */}
      {isPortrait && !dismissPortraitWarning && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(15, 16, 19, 0.98)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            textAlign: 'center',
            color: PocketGridRetroPalette.warmIvoryText,
          }}
        >
          {/* Animated Retro Rotating Phone Graphic */}
          <div
            style={{
              width: '84px',
              height: '84px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: '#1F2025',
              border: `2px solid ${PocketGridRetroPalette.tungstenAmber}`,
              boxShadow: '0 0 28px rgba(217, 148, 59, 0.45)',
              animation: 'pocketGridRotateDevice 2.5s infinite ease-in-out',
            }}
          >
            <svg
              width="46"
              height="46"
              viewBox="0 0 24 24"
              fill="none"
              stroke={PocketGridRetroPalette.tungstenAmber}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="4" y="2" width="16" height="20" rx="3" ry="3" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </div>

          <div
            style={{
              fontSize: '18px',
              fontWeight: 800,
              color: PocketGridRetroPalette.tungstenAmber,
              letterSpacing: '0.04em',
              marginBottom: '8px',
            }}
          >
            PLEASE ROTATE TO LANDSCAPE
          </div>

          <div
            style={{
              fontSize: '13px',
              color: PocketGridRetroPalette.incandescentCream,
              maxWidth: '320px',
              lineHeight: 1.5,
              marginBottom: '24px',
            }}
          >
            PocketGrid Synth is designed to be played horizontally in landscape mode for full access to all controls.
          </div>

          <button
            onClick={async () => {
              await toggleFullscreen();
            }}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              background: PocketGridRetroPalette.phosphorMoss,
              border: `1.5px solid ${PocketGridRetroPalette.incandescentCream}`,
              color: '#FFF',
              fontWeight: 800,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 6px 20px rgba(78, 154, 103, 0.5)',
              marginBottom: '16px',
            }}
          >
            <span>⛶</span>
            <span>ENTER FULLSCREEN LANDSCAPE</span>
          </button>

          <button
            onClick={() => setDismissPortraitWarning(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: PocketGridRetroPalette.mutedPewterText,
              fontSize: '11px',
              textDecoration: 'underline',
              cursor: 'pointer',
              padding: '6px',
            }}
          >
            Dismiss and continue in portrait mode anyway
          </button>
        </div>
      )}

      {/* Top Banner when Portrait is active and full overlay was dismissed */}
      {isPortrait && dismissPortraitWarning && (
        <div
          onClick={async () => {
            await synthEngine.ensureInitialized();
            await toggleFullscreen();
          }}
          style={{
            background: 'linear-gradient(90deg, #7A2824 0%, #B8782E 100%)',
            color: '#FFFFFF',
            padding: '3px 8px',
            fontSize: '9.5px',
            fontWeight: 800,
            textAlign: 'center',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexShrink: 0,
            zIndex: 9999,
            borderBottom: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <span>📱 ROTATE DEVICE TO LANDSCAPE FOR PROPER SYNTH VIEW</span>
          <span
            style={{
              background: 'rgba(0,0,0,0.3)',
              padding: '1px 5px',
              borderRadius: '3px',
              border: '1px solid rgba(255,255,255,0.4)',
            }}
          >
            [TAP TO ROTATE & LOCK]
          </span>
        </div>
      )}

      {/* Toast Notification Banner */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 250,
            background: '#2B2318',
            border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
            color: PocketGridRetroPalette.incandescentCream,
            padding: '6px 16px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 700,
            boxShadow: '0 4px 16px rgba(0,0,0,0.8)',
          }}
        >
          {toastMsg}
        </div>
      )}

      {/* =====================================================================
          1. TOP BAR: NAVIGATION PILLS + METRONOME + PULSING 16/21-STEP SEQ BOX
         ===================================================================== */}
      <div
        style={{
          height: isCompactViewport ? '32px' : '44px',
          minHeight: isCompactViewport ? '32px' : '44px',
          maxHeight: isCompactViewport ? '32px' : '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: isCompactViewport ? '5px' : '8px',
          padding: isCompactViewport ? '2px 8px' : '4px 12px',
          background: '#18191C',
          borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
        }}
      >
        {/* Left Status & Action Pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isCompactViewport ? '4px' : '6px', flexShrink: 0 }}>
          <button
            onClick={() => {
              setSplitSoundPickerTarget('ACTIVE');
              setIsFindSoundsOpen(true);
            }}
            title={activePreset.name}
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 8px' : '0 12px',
              borderRadius: '5px',
              background: PocketGridRetroPalette.brushedTitanium,
              border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
              color: PocketGridRetroPalette.warmIvoryText,
              fontWeight: 700,
              fontSize: isCompactViewport ? '10.5px' : '12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            [🔍 {activePreset.name.length > 22 && isCompactViewport ? activePreset.name.slice(0, 18) + '…' : activePreset.name}]
          </button>

          <button
            onClick={handleSaveLayout}
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 7px' : '0 10px',
              borderRadius: '5px',
              background: PocketGridRetroPalette.brushedTitanium,
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              color: PocketGridRetroPalette.warmIvoryText,
              fontWeight: 700,
              fontSize: isCompactViewport ? '10.5px' : '11.5px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            [💾 SAVE]
          </button>

          {/* Mobile Fullscreen Toggle Pill */}
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen (hides browser URL bar & navigation)'}
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 8px' : '0 10px',
              borderRadius: '5px',
              background: isFullscreen ? '#1E2D27' : PocketGridRetroPalette.brushedTitanium,
              border: `1.5px solid ${
                isFullscreen
                  ? PocketGridRetroPalette.phosphorMoss
                  : PocketGridRetroPalette.tungstenAmber
              }`,
              color: isFullscreen
                ? PocketGridRetroPalette.phosphorMoss
                : PocketGridRetroPalette.tungstenAmber,
              fontWeight: 800,
              fontSize: isCompactViewport ? '10.5px' : '11.5px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>⛶</span>
            <span>{isFullscreen ? 'FULL ON' : 'FULLSCREEN'}</span>
          </button>

          {/* BPM Control Pill with [-] / [TAP] / [+] */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: isCompactViewport ? '28px' : '34px',
              borderRadius: '5px',
              background: PocketGridRetroPalette.brushedTitanium,
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => setBpm((b) => Math.max(40, Number((b - 2).toFixed(1))))}
              style={{
                background: 'transparent',
                border: 'none',
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 800,
                fontSize: isCompactViewport ? '11px' : '13px',
                padding: isCompactViewport ? '0 5px' : '0 8px',
                height: '100%',
                cursor: 'pointer',
              }}
            >
              −
            </button>
            <button
              onClick={handleTapTempo}
              title="Tap 3+ times to set tempo"
              style={{
                background: 'transparent',
                border: 'none',
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 700,
                fontSize: isCompactViewport ? '10.5px' : '12px',
                padding: isCompactViewport ? '0 4px' : '0 6px',
                height: '100%',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {bpm.toFixed(1)} BPM
            </button>
            <button
              onClick={() => setBpm((b) => Math.min(240, Number((b + 2).toFixed(1))))}
              style={{
                background: 'transparent',
                border: 'none',
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 800,
                fontSize: isCompactViewport ? '11px' : '13px',
                padding: isCompactViewport ? '0 5px' : '0 8px',
                height: '100%',
                cursor: 'pointer',
              }}
            >
              +
            </button>
          </div>

          {/* Dedicated METRONOME Toggle Pill */}
          <button
            onClick={() => {
              const next = !isMetronomeOn;
              setIsMetronomeOn(next);
              if (next) {
                void synthEngine.triggerMetronome(true, 0.85);
                showToast('Metronome Enabled');
              } else {
                showToast('Metronome Muted');
              }
            }}
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 8px' : '0 10px',
              borderRadius: '5px',
              background: isMetronomeOn ? '#1F2D27' : PocketGridRetroPalette.brushedTitanium,
              border: `1.5px solid ${
                isMetronomeOn
                  ? PocketGridRetroPalette.phosphorMoss
                  : PocketGridRetroPalette.panelBorder
              }`,
              color: isMetronomeOn
                ? PocketGridRetroPalette.incandescentCream
                : PocketGridRetroPalette.warmIvoryText,
              fontWeight: 700,
              fontSize: isCompactViewport ? '10.5px' : '11.5px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap',
            }}
          >
            <span>⏱ METRO: {isMetronomeOn ? 'ON' : 'OFF'}</span>
            {isMetronomeOn && (
              <span style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>
                {[1, 2, 3, 4].map((b) => (
                  <span
                    key={b}
                    style={{
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      background:
                        isPlaying && currentBeatNumber === b
                          ? b === 1
                            ? PocketGridRetroPalette.tungstenAmber
                            : PocketGridRetroPalette.phosphorMoss
                          : '#3A3E42',
                    }}
                  />
                ))}
              </span>
            )}
          </button>

          {/* Musical Key Selector (Transposes Scale, Keybed & 8 Smart Chords) */}
          <div
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 6px' : '0 9px',
              borderRadius: '5px',
              background: '#1A1813',
              border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: isCompactViewport ? '9px' : '10.5px',
                fontWeight: 800,
                color: PocketGridRetroPalette.tungstenAmber,
                letterSpacing: '0.04em',
              }}
            >
              KEY:
            </span>
            <select
              value={rootKey}
              onChange={(e) => handleRootKeyChange(e.target.value as ChordRootName)}
              title="Set Musical Root Key (transposes scale snap, keybed and 8 Smart Chords)"
              style={{
                background: 'transparent',
                border: 'none',
                color: PocketGridRetroPalette.incandescentCream,
                fontWeight: 800,
                fontSize: isCompactViewport ? '11px' : '12.5px',
                cursor: 'pointer',
                outline: 'none',
                padding: '0 2px',
              }}
            >
              {CHORD_ROOTS.map((k) => (
                <option key={k} value={k} style={{ background: '#1A1813', color: '#FFF' }}>
                  {k}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setScaleIdx((prev) => (prev + 1) % MUSICAL_SCALES.length)}
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 7px' : '0 10px',
              borderRadius: '5px',
              background: PocketGridRetroPalette.brushedTitanium,
              border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              color: PocketGridRetroPalette.warmIvoryText,
              fontWeight: 700,
              fontSize: isCompactViewport ? '10.5px' : '11.5px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            [SCALE: {currentScale.name}]
          </button>

          <button
            onClick={() => {
              const next = !isScaleSnapEnabled;
              setIsScaleSnapEnabled(next);
              showToast(
                next
                  ? `Scale Snap ON: Keybed snaps to ${currentScale.name}`
                  : 'Scale Snap OFF: Full Chromatic Keybed'
              );
            }}
            title="Toggle between Natural Chromatic Keybed (OFF) and Scale Degree Quantization (ON)"
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 7px' : '0 10px',
              borderRadius: '5px',
              background: isScaleSnapEnabled ? '#2C2318' : PocketGridRetroPalette.brushedTitanium,
              border: `1px solid ${
                isScaleSnapEnabled
                  ? PocketGridRetroPalette.tungstenAmber
                  : PocketGridRetroPalette.panelBorder
              }`,
              color: isScaleSnapEnabled
                ? PocketGridRetroPalette.tungstenAmber
                : PocketGridRetroPalette.mutedPewterText,
              fontWeight: 700,
              fontSize: isCompactViewport ? '10.5px' : '11.5px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            [SNAP: {isScaleSnapEnabled ? 'ON' : 'OFF'}]
          </button>

          {/* [⊞ 8-LINE SEQ] Mode Button */}
          <button
            onClick={() => {
              if (deckMode !== 'SEQ_8_LINES') {
                setDeckMode('SEQ_8_LINES');
                setIsSeqModeActive(true);
              } else {
                setDeckMode('SLIDERS');
              }
            }}
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 8px' : '0 11px',
              borderRadius: '5px',
              background:
                deckMode === 'SEQ_8_LINES' ? '#2C2318' : PocketGridRetroPalette.brushedTitanium,
              border: `1.5px solid ${
                deckMode === 'SEQ_8_LINES'
                  ? PocketGridRetroPalette.tungstenAmber
                  : PocketGridRetroPalette.vintageVfdSage
              }`,
              color:
                deckMode === 'SEQ_8_LINES'
                  ? PocketGridRetroPalette.incandescentCream
                  : PocketGridRetroPalette.warmIvoryText,
              fontWeight: 700,
              fontSize: isCompactViewport ? '10.5px' : '11.5px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {deckMode === 'SEQ_8_LINES' ? '[🎛 SLIDER MATRIX]' : '[⊞ 8-LINE SEQUENCER]'}
          </button>

          {/* DAW-Style Last-Touched [🎹 MIDI LEARN] Pill */}
          <button
            onClick={toggleGlobalMidiLearn}
            title={`Last Touched Slider: ${getParamLabel(lastTouchedParamId)} — Move any slider on screen, then turn a knob on your MIDI controller!`}
            style={{
              height: isCompactViewport ? '28px' : '34px',
              padding: isCompactViewport ? '0 8px' : '0 10px',
              borderRadius: '5px',
              background:
                isGlobalMidiLearn || learningParamId
                  ? '#2C2318'
                  : midiStatus.connectedInputs.length > 0
                  ? '#1E2822'
                  : PocketGridRetroPalette.brushedTitanium,
              border: `1.5px solid ${
                isGlobalMidiLearn || learningParamId
                  ? PocketGridRetroPalette.tungstenAmber
                  : midiStatus.connectedInputs.length > 0
                  ? PocketGridRetroPalette.phosphorMoss
                  : PocketGridRetroPalette.panelBorder
              }`,
              color:
                isGlobalMidiLearn || learningParamId
                  ? PocketGridRetroPalette.incandescentCream
                  : PocketGridRetroPalette.warmIvoryText,
              fontWeight: 700,
              fontSize: isCompactViewport ? '10.5px' : '11.5px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background:
                  isGlobalMidiLearn || learningParamId
                    ? PocketGridRetroPalette.tungstenAmber
                    : midiStatus.connectedInputs.length > 0
                    ? PocketGridRetroPalette.phosphorMoss
                    : '#6C6962',
              }}
            />
            <span>
              {isGlobalMidiLearn || learningParamId
                ? `LEARN [${getParamLabel(learningParamId || lastTouchedParamId)}]`
                : `🎹 MIDI LEARN (${getParamLabel(lastTouchedParamId)})`}
            </span>
          </button>
        </div>

        {/* Right Header: In SLIDERS mode, sleek single-line transport; In SEQ_8_LINES mode, pulsing sequencer box */}
        {deckMode === 'SLIDERS' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            {/* Clickable Sequencer State Pill */}
            <button
              onClick={() => {
                setDeckMode('SEQ_8_LINES');
                setIsSeqModeActive(true);
              }}
              title="Click to view 8-Line Sequencer"
              style={{
                height: '28px',
                padding: '0 7px',
                borderRadius: '5px',
                background: isSeqModeActive ? '#261F18' : PocketGridRetroPalette.brushedTitanium,
                border: `1.5px solid ${
                  isSeqModeActive
                    ? isRecording
                      ? PocketGridRetroPalette.studioTapeRed
                      : PocketGridRetroPalette.tungstenAmber
                    : PocketGridRetroPalette.panelBorder
                }`,
                color: isSeqModeActive
                  ? PocketGridRetroPalette.incandescentCream
                  : PocketGridRetroPalette.mutedPewterText,
                fontWeight: 800,
                fontSize: '10px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: isPlaying
                    ? PocketGridRetroPalette.tungstenAmber
                    : isSeqModeActive
                    ? PocketGridRetroPalette.phosphorMoss
                    : '#555',
                }}
              />
              <span>
                {isPlaying
                  ? `STEP ${activeStepIdx + 1}/${stepCountMode}`
                  : `SEQ [${stepCountMode}]`}
              </span>
            </button>

            {/* REC Button */}
            <button
              onClick={async () => {
                await synthEngine.ensureInitialized();
                const nextRec = !isRecording;
                setIsRecording(nextRec);
                if (nextRec && !isPlaying) {
                  setIsPlaying(true);
                }
              }}
              style={{
                height: '28px',
                padding: '0 8px',
                borderRadius: '5px',
                background: isRecording ? PocketGridRetroPalette.studioTapeRed : '#2B1F1E',
                border: `1.5px solid ${
                  isRecording ? '#FF8A80' : PocketGridRetroPalette.studioTapeRed
                }`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 800,
                fontSize: '10.5px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: isRecording ? '0 0 10px rgba(200,62,55,0.85)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: isRecording ? '#FFF3D6' : '#8C2D28',
                }}
              />
              REC
            </button>

            {/* PLAY Button */}
            <button
              onClick={async () => {
                await synthEngine.ensureInitialized();
                setIsPlaying((p) => {
                  const next = !p;
                  if (!next) {
                    stopSequencerPlayback();
                  }
                  return next;
                });
              }}
              style={{
                height: '28px',
                padding: '0 9px',
                borderRadius: '5px',
                background: isPlaying ? PocketGridRetroPalette.phosphorMoss : '#1E2822',
                border: `1.5px solid ${
                  isPlaying ? '#A5D6A7' : PocketGridRetroPalette.phosphorMoss
                }`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 800,
                fontSize: '10.5px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: isPlaying ? '0 0 10px rgba(78,154,103,0.85)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <span>{isPlaying ? '■' : '►'}</span>
              <span>{isPlaying ? 'STOP' : 'PLAY'}</span>
            </button>
          </div>
        ) : (
          /* When in SEQ_8_LINES mode, show the pulsing sequencer step header box */
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '2px 6px',
              borderRadius: '6px',
              background: '#161514',
              border: `1.5px solid ${
                isSeqModeActive
                  ? isRecording
                    ? PocketGridRetroPalette.studioTapeRed
                    : PocketGridRetroPalette.tungstenAmber
                  : PocketGridRetroPalette.panelBorder
              }`,
              animation: isSeqModeActive
                ? `${
                    isRecording ? 'pocketGridRecPulse' : 'pocketGridSeqPulse'
                  } ${beatPulseDurationSec}s infinite ease-in-out`
                : 'none',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2px',
                fontSize: '9px',
                fontWeight: 700,
                gap: '6px',
              }}
            >
              <span
                onClick={() => setIsSeqModeActive((s) => !s)}
                style={{
                  cursor: 'pointer',
                  color: isSeqModeActive
                    ? PocketGridRetroPalette.tungstenAmber
                    : PocketGridRetroPalette.mutedPewterText,
                }}
              >
                SEQ: {isSeqModeActive ? (isRecording ? 'REC ●' : 'ACTIVE ●') : 'OFF ○'}
              </span>

              {/* 16 vs 21 Step Slot Selector */}
              <span
                onClick={() => setStepCountMode((c) => (c === 16 ? 21 : 16))}
                style={{
                  cursor: 'pointer',
                  color: PocketGridRetroPalette.incandescentCream,
                  background: '#2C2318',
                  padding: '0 4px',
                  borderRadius: '3px',
                  border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                }}
              >
                SLOTS: {stepCountMode === 16 ? '●16 | ○21' : '○16 | ●21'}
              </span>

              <span
                onClick={() => setDeckMode('SLIDERS')}
                style={{
                  cursor: 'pointer',
                  color: PocketGridRetroPalette.vintageVfdSage,
                  textDecoration: 'underline',
                }}
              >
                SLIDERS ▲
              </span>
            </div>

            <div style={{ display: 'flex', gap: '2px' }}>
              {Array.from({ length: stepCountMode }).map((_, idx) => {
                const isCurrentPlayhead = activeStepIdx === idx;
                const anyTrackActiveOnStep = seqLines.some(
                  (line) => !line.muted && line.steps[idx]?.active
                );
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      pushUndoSnapshot();
                      setSeqLines((prev) =>
                        prev.map((line, lIdx) => {
                          if (lIdx !== selectedSeqLineIdx) return line;
                          const willBeActive = !line.steps[idx].active;
                          if (willBeActive) {
                            triggerLineSound(line, line.defaultNotes);
                          }
                          return {
                            ...line,
                            steps: line.steps.map((st, sIdx) =>
                              sIdx === idx
                                ? {
                                    ...st,
                                    active: willBeActive,
                                    notes: [...line.defaultNotes],
                                    chordLabel: line.defaultChordLabel,
                                  }
                                : st
                            ),
                          };
                        })
                      );
                    }}
                    style={{
                      minWidth: stepCountMode === 21 ? '17px' : '20px',
                      height: '18px',
                      padding: 0,
                      borderRadius: '3px',
                      background: isCurrentPlayhead
                        ? PocketGridRetroPalette.incandescentCream
                        : anyTrackActiveOnStep
                        ? '#3A2816'
                        : '#222120',
                      border: `1px solid ${
                        isCurrentPlayhead
                          ? '#FFFFFF'
                          : anyTrackActiveOnStep
                          ? PocketGridRetroPalette.tungstenAmber
                          : '#3A3835'
                      }`,
                      color: isCurrentPlayhead
                        ? '#141517'
                        : anyTrackActiveOnStep
                        ? PocketGridRetroPalette.incandescentCream
                        : PocketGridRetroPalette.mutedPewterText,
                      fontWeight: 700,
                      fontSize: '8.5px',
                      cursor: 'pointer',
                    }}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* =====================================================================
          2. MIDDLE DECK: SWAPPABLE BETWEEN:
             (A) 8-LINE × (16 OR 21)-STEP MULTI-TRACK TOUCH SEQUENCER (WITH FULL CHORD SUPPORT)
             (B) 3 SWAPPABLE SLIDER / PAD / VISUALIZER BAYS
             + RIGHT TRANSPORT & SMART CHORD STRIP ALWAYS VISIBLE
         ===================================================================== */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          gap: isCompactViewport ? '6px' : '12px',
          padding: isCompactViewport ? '2px 8px' : '10px 14px',
          minHeight: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {deckMode === 'SEQ_8_LINES' ? (
          /* ===================================================================
             8-LINE × (16 OR 21)-STEP MULTI-TRACK TOUCH SEQUENCER MATRIX
             =================================================================== */
          <div
            style={{
              flex: 1,
              background: PocketGridRetroPalette.brushedTitanium,
              border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
              borderRadius: '8px',
              padding: '5px 8px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: 0,
            }}
          >
            {/* 8-Line Matrix Header Bar */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                paddingBottom: '3px',
                marginBottom: '3px',
                fontSize: '10px',
                fontWeight: 700,
                flexWrap: 'wrap',
                gap: '6px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ color: PocketGridRetroPalette.tungstenAmber, fontWeight: 800 }}>
                  ⊞ SEQUENCER:
                </span>
                <input
                  type="text"
                  value={seqCustomTitle}
                  onChange={(e) => setSeqCustomTitle(e.target.value)}
                  placeholder="Name sequence..."
                  title="Name this saved sequence"
                  style={{
                    height: '20px',
                    width: isCompactViewport ? '110px' : '150px',
                    padding: '0 5px',
                    borderRadius: '3px',
                    background: '#131417',
                    border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                    color: PocketGridRetroPalette.incandescentCream,
                    fontSize: '10px',
                    fontWeight: 700,
                  }}
                />
                <button
                  onClick={() => handleSaveSequenceToBank(activeSeqBankSlot)}
                  title={`Save sequence "${seqCustomTitle}" to current Slot #${activeSeqBankSlot}`}
                  style={{
                    padding: '2px 7px',
                    borderRadius: '3px',
                    background: PocketGridRetroPalette.phosphorMoss,
                    border: `1px solid ${PocketGridRetroPalette.incandescentCream}`,
                    color: PocketGridRetroPalette.incandescentCream,
                    fontSize: '9.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  💾 SAVE #{activeSeqBankSlot}
                </button>
                <button
                  onClick={() => setIsSaveSeqModalOpen(true)}
                  title="Open Sequence Bank & Slot Manager (1-8)"
                  style={{
                    padding: '2px 7px',
                    borderRadius: '3px',
                    background: '#242018',
                    border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                    color: PocketGridRetroPalette.tungstenAmber,
                    fontSize: '9.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  🗂 BANK SLOTS
                </button>

                <span style={{ color: PocketGridRetroPalette.mutedPewterText, fontSize: '9px', marginLeft: '2px' }}>
                  Brush:{' '}
                  <strong style={{ color: PocketGridRetroPalette.incandescentCream }}>
                    {seqLines[selectedSeqLineIdx]?.label}{' '}
                    {seqLines[selectedSeqLineIdx]?.kind === 'synth'
                      ? seqLines[selectedSeqLineIdx]?.defaultChordLabel
                        ? `[${seqLines[selectedSeqLineIdx].defaultChordLabel}]`
                        : `[${formatMidiNote(seqLines[selectedSeqLineIdx].defaultNotes[0])}]`
                      : ''}
                  </strong>
                </span>
              </div>

              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                {/* 16 vs 21 Step Slot Toggle */}
                <button
                  onClick={() => setStepCountMode((c) => (c === 16 ? 21 : 16))}
                  style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: '#2C2318',
                    border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                    color: PocketGridRetroPalette.incandescentCream,
                    fontSize: '10px',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  [SLOTS: {stepCountMode === 16 ? '● 16 | ○ 21' : '○ 16 | ● 21'}]
                </button>

                <button
                  onClick={() => {
                    pushUndoSnapshot();
                    setSeqLines((prev) =>
                      prev.map((line) => ({
                        ...line,
                        steps: line.steps.map((s) => ({ ...s, active: false })),
                      }))
                    );
                    showToast('Cleared All 8 Sequence Lines');
                  }}
                  style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: '#2B1F1E',
                    border: `1px solid ${PocketGridRetroPalette.studioTapeRed}`,
                    color: PocketGridRetroPalette.warmIvoryText,
                    fontSize: '10px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  [🗑 CLEAR ALL]
                </button>

                <button
                  onClick={() => setDeckMode('SLIDERS')}
                  style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: '#1E2822',
                    border: `1px solid ${PocketGridRetroPalette.phosphorMoss}`,
                    color: PocketGridRetroPalette.incandescentCream,
                    fontSize: '10px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  [🎛 SLIDER MATRIX]
                </button>
              </div>
            </div>

            {/* BUS MASTER CONTROL BAR: DRUM BUS & SYNTH BUS */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#121316',
                border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                borderRadius: '5px',
                padding: '2px 6px',
                marginBottom: '3px',
                gap: '8px',
                flexWrap: 'wrap',
                flexShrink: 0,
              }}
            >
              {/* DRUM BUS CONTROLS */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span
                  style={{
                    fontSize: isCompactViewport ? '8.5px' : '9.5px',
                    fontWeight: 800,
                    color: PocketGridRetroPalette.tungstenAmber,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  🥁 DRUM BUS ({seqLines.filter((l) => (l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'DRUMS').length} TRK):
                </span>
                <button
                  onClick={() => handleToggleBusMute('DRUMS')}
                  title="Toggle Mute on all Drum tracks simultaneously"
                  style={{
                    padding: '1px 6px',
                    borderRadius: '3px',
                    fontSize: '8.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    background: isDrumBusAllMuted ? '#4E1F1E' : '#222328',
                    border: `1px solid ${
                      isDrumBusAllMuted
                        ? PocketGridRetroPalette.studioTapeRed
                        : PocketGridRetroPalette.panelBorder
                    }`,
                    color: isDrumBusAllMuted
                      ? PocketGridRetroPalette.studioTapeRed
                      : PocketGridRetroPalette.warmIvoryText,
                  }}
                >
                  {isDrumBusAllMuted ? '🔇 MUTED' : 'MUTE ALL'}
                </button>
                <button
                  onClick={() => handleToggleBusSolo('DRUMS')}
                  title="Toggle Solo on all Drum tracks simultaneously"
                  style={{
                    padding: '1px 6px',
                    borderRadius: '3px',
                    fontSize: '8.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    background: isDrumBusAllSoloed ? '#1C382A' : '#222328',
                    border: `1px solid ${
                      isDrumBusAllSoloed
                        ? PocketGridRetroPalette.phosphorMoss
                        : PocketGridRetroPalette.panelBorder
                    }`,
                    color: isDrumBusAllSoloed
                      ? PocketGridRetroPalette.phosphorMoss
                      : PocketGridRetroPalette.warmIvoryText,
                  }}
                >
                  {isDrumBusAllSoloed ? '★ SOLO ON' : 'SOLO ALL'}
                </button>
                <button
                  onClick={() => handleClearBusSteps('DRUMS')}
                  title="Clear all active steps across the Drum Bus"
                  style={{
                    padding: '1px 5px',
                    borderRadius: '3px',
                    fontSize: '8.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: '#241B1B',
                    border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                    color: PocketGridRetroPalette.mutedPewterText,
                  }}
                >
                  CLEAR
                </button>
              </div>

              <div style={{ width: '1px', height: '14px', background: PocketGridRetroPalette.panelBorder }} />

              {/* SYNTH BUS CONTROLS */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span
                  style={{
                    fontSize: isCompactViewport ? '8.5px' : '9.5px',
                    fontWeight: 800,
                    color: PocketGridRetroPalette.vintageVfdSage,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  🎹 SYNTH BUS ({seqLines.filter((l) => (l.bus || (l.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'SYNTHS').length} TRK):
                </span>
                <button
                  onClick={() => handleToggleBusMute('SYNTHS')}
                  title="Toggle Mute on all Synth tracks simultaneously"
                  style={{
                    padding: '1px 6px',
                    borderRadius: '3px',
                    fontSize: '8.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    background: isSynthBusAllMuted ? '#4E1F1E' : '#222328',
                    border: `1px solid ${
                      isSynthBusAllMuted
                        ? PocketGridRetroPalette.studioTapeRed
                        : PocketGridRetroPalette.panelBorder
                    }`,
                    color: isSynthBusAllMuted
                      ? PocketGridRetroPalette.studioTapeRed
                      : PocketGridRetroPalette.warmIvoryText,
                  }}
                >
                  {isSynthBusAllMuted ? '🔇 MUTED' : 'MUTE ALL'}
                </button>
                <button
                  onClick={() => handleToggleBusSolo('SYNTHS')}
                  title="Toggle Solo on all Synth tracks simultaneously"
                  style={{
                    padding: '1px 6px',
                    borderRadius: '3px',
                    fontSize: '8.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    background: isSynthBusAllSoloed ? '#1C382A' : '#222328',
                    border: `1px solid ${
                      isSynthBusAllSoloed
                        ? PocketGridRetroPalette.phosphorMoss
                        : PocketGridRetroPalette.panelBorder
                    }`,
                    color: isSynthBusAllSoloed
                      ? PocketGridRetroPalette.phosphorMoss
                      : PocketGridRetroPalette.warmIvoryText,
                  }}
                >
                  {isSynthBusAllSoloed ? '★ SOLO ON' : 'SOLO ALL'}
                </button>
                <button
                  onClick={() => handleClearBusSteps('SYNTHS')}
                  title="Clear all active steps across the Synth Bus"
                  style={{
                    padding: '1px 5px',
                    borderRadius: '3px',
                    fontSize: '8.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: '#241B1B',
                    border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                    color: PocketGridRetroPalette.mutedPewterText,
                  }}
                >
                  CLEAR
                </button>
              </div>
            </div>

            {/* 8 Touchable Rows (Each Row = Sound Picker + Note/Full-Chord Selector + Mute + 16 or 21 Step Spaces) */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '2px',
                minHeight: 0,
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x',
              }}
            >
              {seqLines.map((line, lineIdx) => {
                const isSelectedLine = selectedSeqLineIdx === lineIdx;
                const anyTrackSoloed = seqLines.some((l) => l.soloed);
                const isSilencedBySoloOrMute = anyTrackSoloed ? !line.soloed || line.muted : line.muted;
                return (
                  <div
                    key={line.id}
                    onClick={() => setSelectedSeqLineIdx(lineIdx)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '1px 4px',
                      borderRadius: '4px',
                      background: isSelectedLine ? '#23201B' : '#18191C',
                      border: `1px solid ${
                        isSelectedLine ? line.accentColor : PocketGridRetroPalette.panelBorder
                      }`,
                      opacity: isSilencedBySoloOrMute ? 0.42 : 1.0,
                      transition: 'opacity 100ms ease',
                      minHeight: 0,
                      minWidth: isCompactViewport ? (stepCountMode === 21 ? '820px' : '680px') : '100%',
                    }}
                  >
                    {/* Left Track Control Header: Line # + Sound Dropdown + Note/Chord Brush Selector + Solo + Mute + Gate */}
                    <div
                      style={{
                        width: isCompactViewport ? '180px' : '264px',
                        minWidth: isCompactViewport ? '180px' : '264px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        position: 'sticky',
                        left: 0,
                        zIndex: 4,
                        background: isSelectedLine ? '#23201B' : '#18191C',
                        paddingRight: '4px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 800,
                          color: isSelectedLine
                            ? PocketGridRetroPalette.incandescentCream
                            : line.accentColor,
                          width: '16px',
                        }}
                      >
                        L{lineIdx + 1}
                      </span>

                      {/* Sound Selector Dropdown */}
                      <select
                        value={
                          line.kind === 'drum' ? `drum:${line.drumIdx}` : `synth:${line.presetId}`
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          setSeqLines((prev) =>
                            prev.map((l, idx) => {
                              if (idx !== lineIdx) return l;
                              if (val.startsWith('drum:')) {
                                const dIdx = Number(val.split(':')[1]);
                                const updated: SequencerTrackLine = {
                                  ...l,
                                  kind: 'drum',
                                  drumIdx: dIdx,
                                  label: `LINE ${idx + 1}: ${DRUM_LABELS[dIdx]}`,
                                };
                                triggerLineSound(updated);
                                return updated;
                              } else {
                                const pId = val.split(':')[1];
                                const found =
                                  presets.find((p) => p.id === pId) || FACTORY_PRESETS[0];
                                const updated: SequencerTrackLine = {
                                  ...l,
                                  kind: 'synth',
                                  presetId: found.id,
                                  label: `LINE ${idx + 1}: ${found.name
                                    .replace(/^\d+\.\s*/, '')
                                    .slice(0, 11)}`,
                                };
                                triggerLineSound(updated, updated.defaultNotes);
                                return updated;
                              }
                            })
                          );
                        }}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          height: '20px',
                          background: '#121315',
                          color: PocketGridRetroPalette.warmIvoryText,
                          border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                          borderRadius: '3px',
                          fontSize: '9px',
                          fontWeight: 700,
                          padding: '0 2px',
                          cursor: 'pointer',
                        }}
                      >
                        <optgroup label="🥁 DRUM SOUNDS">
                          {DRUM_LABELS.map((dName, dIdx) => (
                            <option key={`drum:${dIdx}`} value={`drum:${dIdx}`}>
                              🥁 {dName}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="🎹 SYNTH & USER SOUNDS">
                          {presets.map((p) => (
                            <option key={`synth:${p.id}`} value={`synth:${p.id}`}>
                              {p.isUserSlot ? '👤' : '🎹'} {p.name}
                            </option>
                          ))}
                        </optgroup>
                      </select>

                      {/* Single Note OR Full Chord Brush Selector for Melodic Lines */}
                      {line.kind === 'synth' && (
                        <select
                          value={
                            line.defaultChordLabel
                              ? `chord:${line.defaultChordLabel}`
                              : `note:${line.defaultNotes[0] ?? 62}`
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            setSeqLines((prev) =>
                              prev.map((l, idx) => {
                                if (idx !== lineIdx) return l;
                                if (val.startsWith('chord:')) {
                                  const cName = val.split(':')[1];
                                  const foundChord =
                                    smartChords.find((c) => c.label === cName) ||
                                    currentScale.chords.find((c) => c.label === cName);
                                  const chordNotes = foundChord
                                    ? foundChord.offsets.map((o) => 62 + o)
                                    : [62, 69, 72, 76];
                                  const updated = {
                                    ...l,
                                    defaultNotes: chordNotes,
                                    defaultChordLabel: cName,
                                  };
                                  triggerLineSound(updated, chordNotes);
                                  return updated;
                                } else {
                                  const midiN = Number(val.split(':')[1]);
                                  const updated = {
                                    ...l,
                                    defaultNotes: [midiN],
                                    defaultChordLabel: undefined,
                                  };
                                  triggerLineSound(updated, [midiN]);
                                  return updated;
                                }
                              })
                            );
                          }}
                          title="Choose a Single Note OR a Full Chord to place into any single step space!"
                          style={{
                            width: '62px',
                            height: '20px',
                            background: line.defaultChordLabel ? '#2C2318' : '#1E2822',
                            color: line.defaultChordLabel
                              ? PocketGridRetroPalette.tungstenAmber
                              : PocketGridRetroPalette.vintageVfdSage,
                            border: `1px solid ${
                              line.defaultChordLabel
                                ? PocketGridRetroPalette.tungstenAmber
                                : PocketGridRetroPalette.vintageVfdSage
                            }`,
                            borderRadius: '3px',
                            fontSize: '9px',
                            fontWeight: 800,
                            padding: '0 2px',
                            cursor: 'pointer',
                          }}
                        >
                          <optgroup label="🎼 FULL CHORDS (8 SMART CHORDS)">
                            {smartChords.map((ch) => (
                              <option key={`chord:${ch.label}`} value={`chord:${ch.label}`}>
                                {ch.label}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="🎵 SINGLE NOTES">
                            {[36, 38, 41, 43, 45, 48, 50, 53, 55, 57, 60, 62, 65, 67, 69, 72, 74].map(
                              (n) => (
                                <option key={`note:${n}`} value={`note:${n}`}>
                                  {formatMidiNote(n)}
                                </option>
                              )
                            )}
                          </optgroup>
                        </select>
                      )}

                      {/* Solo Line Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSeqLines((prev) =>
                            prev.map((l, idx) => (idx === lineIdx ? { ...l, soloed: !l.soloed } : l))
                          );
                        }}
                        title={
                          line.soloed
                            ? 'Unsolo this line'
                            : 'Solo this line (isolates this track in the sequencer groove)'
                        }
                        style={{
                          width: '18px',
                          height: '18px',
                          padding: 0,
                          borderRadius: '3px',
                          background: line.soloed
                            ? PocketGridRetroPalette.tungstenAmber
                            : '#25272C',
                          border: `1px solid ${
                            line.soloed
                              ? PocketGridRetroPalette.tungstenAmber
                              : PocketGridRetroPalette.panelBorder
                          }`,
                          color: line.soloed ? '#1E1F22' : PocketGridRetroPalette.warmIvoryText,
                          fontSize: '9px',
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        S
                      </button>

                      {/* Mute Line Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSeqLines((prev) =>
                            prev.map((l, idx) => (idx === lineIdx ? { ...l, muted: !l.muted } : l))
                          );
                        }}
                        title="Mute / Unmute this line"
                        style={{
                          width: '18px',
                          height: '18px',
                          padding: 0,
                          borderRadius: '3px',
                          background: line.muted
                            ? PocketGridRetroPalette.studioTapeRed
                            : '#25272C',
                          border: `1px solid ${
                            line.muted
                              ? PocketGridRetroPalette.studioTapeRed
                              : PocketGridRetroPalette.panelBorder
                          }`,
                          color: PocketGridRetroPalette.warmIvoryText,
                          fontSize: '9px',
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        M
                      </button>

                      {/* Sub-mix Bus Group Selector */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const curBus = line.bus || (line.kind === 'drum' ? 'DRUMS' : 'SYNTHS');
                          const nextBus = curBus === 'DRUMS' ? 'SYNTHS' : 'DRUMS';
                          setSeqLines((prev) =>
                            prev.map((l, idx) => (idx === lineIdx ? { ...l, bus: nextBus } : l))
                          );
                          showToast(`L${lineIdx + 1} routed to ${nextBus === 'DRUMS' ? '🥁 DRUM BUS' : '🎹 SYNTH BUS'}`);
                        }}
                        title={`Currently routed to ${line.bus || (line.kind === 'drum' ? 'DRUMS' : 'SYNTHS')} Bus. Click to change group.`}
                        style={{
                          height: '18px',
                          padding: '0 3px',
                          borderRadius: '3px',
                          background:
                            (line.bus || (line.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'DRUMS'
                              ? '#2C2216'
                              : '#1A2923',
                          border: `1px solid ${
                            (line.bus || (line.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'DRUMS'
                              ? PocketGridRetroPalette.tungstenAmber
                              : PocketGridRetroPalette.vintageVfdSage
                          }`,
                          color:
                            (line.bus || (line.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'DRUMS'
                              ? PocketGridRetroPalette.tungstenAmber
                              : PocketGridRetroPalette.vintageVfdSage,
                          fontSize: '8px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {(line.bus || (line.kind === 'drum' ? 'DRUMS' : 'SYNTHS')) === 'DRUMS' ? '🥁' : '🎹'}
                      </button>

                      {/* Gate Length / Sound Duration Multiplier for Synth Lines */}
                      {line.kind === 'synth' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const nextGate = line.gateSteps === 1 ? 2 : line.gateSteps === 2 ? 4 : line.gateSteps === 4 ? 8 : 1;
                            setSeqLines((prev) =>
                              prev.map((l, idx) => (idx === lineIdx ? { ...l, gateSteps: nextGate } : l))
                            );
                            showToast(
                              `L${lineIdx + 1} Gate: ${
                                nextGate === 1
                                  ? '1 Step (¼ Beat)'
                                  : nextGate === 2
                                  ? '2 Steps (½ Beat)'
                                  : nextGate === 4
                                  ? '4 Steps (1 Beat)'
                                  : '8 Steps (2 Beats)'
                              }`
                            );
                          }}
                          title={`Gate Length: ${
                            line.gateSteps === 1 || !line.gateSteps
                              ? '1 Step (¼ Beat)'
                              : line.gateSteps === 2
                              ? '2 Steps (½ Beat)'
                              : line.gateSteps === 4
                              ? '4 Steps (1 Beat)'
                              : '8 Steps (2 Beats)'
                          }. Click to cycle.`}
                          style={{
                            height: '18px',
                            padding: '0 3px',
                            borderRadius: '3px',
                            background: line.gateSteps && line.gateSteps > 1 ? '#2E2214' : '#1C1D21',
                            border: `1px solid ${
                              line.gateSteps && line.gateSteps > 1
                                ? PocketGridRetroPalette.tungstenAmber
                                : PocketGridRetroPalette.panelBorder
                            }`,
                            color:
                              line.gateSteps && line.gateSteps > 1
                                ? PocketGridRetroPalette.tungstenAmber
                                : PocketGridRetroPalette.mutedPewterText,
                            fontSize: '8px',
                            fontWeight: 800,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {line.gateSteps === 2
                            ? '½B'
                            : line.gateSteps === 4
                            ? '1B'
                            : line.gateSteps === 8
                            ? '2B'
                            : '¼B'}
                        </button>
                      )}
                    </div>

                    {/* 16 or 21 Touchable Step Spaces for This Line (Horizontally Scrollable) */}
                    <div
                      style={{
                        flex: 1,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${stepCountMode}, minmax(${isCompactViewport ? '28px' : '34px'}, 1fr))`,
                        gap: '2px',
                        height: '100%',
                        alignItems: 'center',
                      }}
                    >
                      {line.steps.slice(0, stepCountMode).map((cell, stepIdx) => {
                        const isPlayhead = activeStepIdx === stepIdx;
                        const isBeatStart = stepIdx % 4 === 0;
                        const isChordCell =
                          cell.active && (Boolean(cell.chordLabel) || cell.notes.length > 1);
                        return (
                          <button
                            key={stepIdx}
                            onPointerDown={(e) => {
                              e.stopPropagation();
                              setSelectedSeqLineIdx(lineIdx);
                              pushUndoSnapshot();
                              const nextActive = !cell.active;
                              if (nextActive) {
                                triggerLineSound(line, line.defaultNotes);
                              }
                              setSeqLines((prev) =>
                                prev.map((l, lIdx) =>
                                  lIdx === lineIdx
                                    ? {
                                        ...l,
                                        steps: l.steps.map((st, sIdx) =>
                                          sIdx === stepIdx
                                            ? {
                                                active: nextActive,
                                                notes: [...l.defaultNotes],
                                                chordLabel: l.defaultChordLabel,
                                                velocity: 0.88,
                                              }
                                            : st
                                        ),
                                      }
                                    : l
                                )
                              );
                            }}
                            style={{
                              height: '86%',
                              minHeight: '16px',
                              padding: 0,
                              borderRadius: '3px',
                              opacity: line.muted ? 0.35 : 1,
                              background: isPlayhead
                                ? cell.active
                                  ? PocketGridRetroPalette.incandescentCream
                                  : '#4A463F'
                                : cell.active
                                ? isChordCell
                                  ? PocketGridRetroPalette.tungstenAmber
                                  : line.accentColor
                                : isBeatStart
                                ? '#26282D'
                                : '#1B1C20',
                              border: `1px solid ${
                                isPlayhead
                                  ? '#FFFFFF'
                                  : cell.active
                                  ? PocketGridRetroPalette.incandescentCream
                                  : isBeatStart
                                  ? '#3E4149'
                                  : '#2A2C32'
                              }`,
                              color:
                                isPlayhead || cell.active
                                  ? '#141517'
                                  : PocketGridRetroPalette.mutedPewterText,
                              fontSize: stepCountMode === 21 ? '7px' : '8px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            {cell.active
                              ? line.kind === 'synth'
                                ? cell.chordLabel || formatMidiNote(cell.notes[0] ?? 60)
                                : '●'
                              : stepIdx + 1}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ===================================================================
             HIERARCHICAL SYNTHESIS CONTROL SURFACE (TOP 16 / TOP 32 / CHARACTER DEEP)
             =================================================================== */
          <div
            style={{
              flex: 1,
              background: PocketGridRetroPalette.brushedTitanium,
              border: `1.5px solid ${
                mainControlSurfaceTab === 'CHARACTER_DEEP'
                  ? PocketGridRetroPalette.champagneBrass
                  : PocketGridRetroPalette.tungstenAmber
              }`,
              borderRadius: isCompactViewport ? '6px' : '8px',
              padding: isCompactViewport ? '3px 6px' : '10px 14px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: isCompactViewport ? '2px' : '8px',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* Header: Tab Navigation [ CORE ENGINE ] | [ CHARACTER & RESONATORS ] + Mode Toggle + Further Refine + Drum Pads */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                paddingBottom: '2px',
                marginBottom: '2px',
                flexWrap: 'wrap',
                gap: '4px',
              }}
            >
              {/* Primary Tabs */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    background: '#16171A',
                    border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                    borderRadius: '5px',
                    padding: '2px',
                    gap: '2px',
                  }}
                >
                  <button
                    onClick={() => setMainControlSurfaceTab('CORE')}
                    style={{
                      padding: '3px 9px',
                      borderRadius: '4px',
                      background: mainControlSurfaceTab === 'CORE' ? '#2E2519' : 'transparent',
                      border: `1px solid ${
                        mainControlSurfaceTab === 'CORE'
                          ? PocketGridRetroPalette.tungstenAmber
                          : 'transparent'
                      }`,
                      color:
                        mainControlSurfaceTab === 'CORE'
                          ? PocketGridRetroPalette.tungstenAmber
                          : PocketGridRetroPalette.mutedPewterText,
                      fontSize: '10px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    [ CORE ENGINE ]
                  </button>
                  <button
                    onClick={() => setMainControlSurfaceTab('CHARACTER_DEEP')}
                    style={{
                      padding: '3px 9px',
                      borderRadius: '4px',
                      background: mainControlSurfaceTab === 'CHARACTER_DEEP' ? '#2B2319' : 'transparent',
                      border: `1px solid ${
                        mainControlSurfaceTab === 'CHARACTER_DEEP'
                          ? PocketGridRetroPalette.champagneBrass
                          : 'transparent'
                      }`,
                      color:
                        mainControlSurfaceTab === 'CHARACTER_DEEP'
                          ? PocketGridRetroPalette.champagneBrass
                          : PocketGridRetroPalette.mutedPewterText,
                      fontSize: '10px',
                      fontWeight: 800,
                      cursor: 'pointer',
                    }}
                  >
                    [ CHARACTER & RESONATORS ]
                  </button>
                </div>

                {mainControlSurfaceTab === 'CORE' && (
                  <div
                    style={{
                      display: 'inline-flex',
                      background: '#16171A',
                      border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                      borderRadius: '5px',
                      padding: '2px',
                      gap: '2px',
                    }}
                  >
                    <button
                      onClick={() => setCoreSurfaceMode('TOP_16')}
                      style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        background: coreSurfaceMode === 'TOP_16' ? '#1E2822' : 'transparent',
                        border: `1px solid ${
                          coreSurfaceMode === 'TOP_16'
                            ? PocketGridRetroPalette.phosphorMoss
                            : 'transparent'
                        }`,
                        color:
                          coreSurfaceMode === 'TOP_16'
                            ? PocketGridRetroPalette.incandescentCream
                            : PocketGridRetroPalette.mutedPewterText,
                        fontSize: '10px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      TOP 16 (LIVE)
                    </button>
                    <button
                      onClick={() => setCoreSurfaceMode('TOP_32')}
                      style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        background: coreSurfaceMode === 'TOP_32' ? '#1F2A2E' : 'transparent',
                        border: `1px solid ${
                          coreSurfaceMode === 'TOP_32'
                            ? PocketGridRetroPalette.electricTeal
                            : 'transparent'
                        }`,
                        color:
                          coreSurfaceMode === 'TOP_32'
                            ? PocketGridRetroPalette.incandescentCream
                            : PocketGridRetroPalette.mutedPewterText,
                        fontSize: '10px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      TOP 32 (STUDIO MATRIX)
                    </button>
                  </div>
                )}
              </div>

              {/* Right Side Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {mainControlSurfaceTab === 'CORE' ? (
                  <button
                    onClick={() => setMainControlSurfaceTab('CHARACTER_DEEP')}
                    style={{
                      padding: '3px 9px',
                      borderRadius: '4px',
                      background: '#241E14',
                      border: `1.5px solid ${PocketGridRetroPalette.champagneBrass}`,
                      color: PocketGridRetroPalette.champagneBrass,
                      fontSize: '10px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    🎛 Further Refine Sound ◄
                  </button>
                ) : (
                  <button
                    onClick={() => setMainControlSurfaceTab('CORE')}
                    style={{
                      padding: '3px 9px',
                      borderRadius: '4px',
                      background: '#1C241F',
                      border: `1.5px solid ${PocketGridRetroPalette.phosphorMoss}`,
                      color: PocketGridRetroPalette.phosphorMoss,
                      fontSize: '10px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    ◄ Back to Core Engine
                  </button>
                )}

                <button
                  onClick={() => setShowDrumPads((prev) => !prev)}
                  title={showDrumPads ? 'Hide inline Drum Pads next to Chords' : 'Show inline 6-Pad Drum Rack next to Chords'}
                  style={{
                    padding: '3px 8px',
                    borderRadius: '4px',
                    background: showDrumPads ? PocketGridRetroPalette.tungstenAmber : '#1F1B18',
                    border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                    color: showDrumPads ? '#141517' : PocketGridRetroPalette.warmIvoryText,
                    fontSize: '10px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: showDrumPads ? '0 0 8px rgba(217, 148, 59, 0.45)' : 'none',
                  }}
                >
                  🥁 {showDrumPads ? 'DRUMS: ON' : 'DRUMS'}
                </button>
              </div>
            </div>

            {/* View A: TOP 16 PERFORMANCE LAYOUT (4 Clusters of 4) */}
            {mainControlSurfaceTab === 'CORE' && coreSurfaceMode === 'TOP_16' && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  gap: isCompactViewport ? '2px' : '6px',
                  minHeight: 0,
                  overflowY: 'auto',
                }}
              >
                {[
                  {
                    label: 'ROW 1: TONE & HARMONICS (OSC / FILTER TIMBRE)',
                    accent: PocketGridRetroPalette.tungstenAmber,
                    slice: [0, 4] as const,
                  },
                  {
                    label: 'ROW 2: DYNAMIC ARTICULATION (AMPLITUDE & FILTER TIME)',
                    accent: PocketGridRetroPalette.vintageVfdSage,
                    slice: [4, 8] as const,
                  },
                  {
                    label: 'ROW 3: ANIMATION & MODULATION (LFO & MOVEMENT)',
                    accent: PocketGridRetroPalette.burntTerracotta,
                    slice: [8, 12] as const,
                  },
                  {
                    label: 'ROW 4: SPACE, DRIVE & MASTER OUTPUT',
                    accent: PocketGridRetroPalette.champagneBrass,
                    slice: [12, 16] as const,
                  },
                ].map((row, rIdx) => {
                  const rowParams = TOP_16_PARAMS.slice(row.slice[0], row.slice[1]);
                  return (
                    <div
                      key={rIdx}
                      style={{
                        minHeight: isCompactViewport ? '22px' : '44px',
                        maxHeight: isCompactViewport ? '34px' : '68px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        background: '#151619',
                        borderRadius: isCompactViewport ? '3px' : '6px',
                        border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                        padding: isCompactViewport ? '1px 4px' : '4px 12px',
                        boxSizing: 'border-box',
                      }}
                    >
                      <div
                        style={{
                          fontSize: isCompactViewport ? '7.5px' : '11px',
                          fontWeight: 800,
                          color: row.accent,
                          marginBottom: isCompactViewport ? '0px' : '6px',
                          letterSpacing: '0.04em',
                          lineHeight: 1.1,
                        }}
                      >
                        {row.label}
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: isCompactViewport ? '4px' : '18px',
                          alignItems: 'center',
                        }}
                      >
                        {rowParams.map((param) => {
                          const val = paramValues[param.id] ?? param.defaultValue;
                          return (
                            <PrecisionHorizontalSlider
                              key={param.id}
                              label={param.name}
                              formattedValue={param.formatDisplay(val)}
                              value={val}
                              defaultValue={param.defaultValue}
                              accentColor={row.accent}
                              compact={isCompactViewport}
                              midiCcNumber={paramToCcMap[param.id] ?? param.defaultMidiCc}
                              isMidiLearning={learningParamId === param.id}
                              onToggleMidiLearn={() => toggleMidiLearnForParam(param.id)}
                              onChange={(v) => handleParamChange(param.id, v)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* View B: TOP 32 EXTENDED STUDIO SYNTHESIS MATRIX (6 Modular Channel Strips) */}
            {mainControlSurfaceTab === 'CORE' && coreSurfaceMode === 'TOP_32' && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  gap: '6px',
                  overflowX: 'auto',
                  minHeight: 0,
                  paddingBottom: '2px',
                }}
              >
                {[
                  {
                    title: '1. OSC 1 & 2 ENGINE',
                    accent: PocketGridRetroPalette.tungstenAmber,
                    slice: [0, 8] as const,
                  },
                  {
                    title: '2. MIXER & PRE-DRIVE',
                    accent: PocketGridRetroPalette.burntTerracotta,
                    slice: [8, 12] as const,
                  },
                  {
                    title: '3. VCF / FILTER LAB',
                    accent: PocketGridRetroPalette.vintageVfdSage,
                    slice: [12, 17] as const,
                  },
                  {
                    title: '4. ENVELOPES (ADSR × 2)',
                    accent: PocketGridRetroPalette.electricTeal,
                    slice: [17, 25] as const,
                  },
                  {
                    title: '5. LFO MOD MATRIX',
                    accent: PocketGridRetroPalette.champagneBrass,
                    slice: [25, 29] as const,
                  },
                  {
                    title: '6. MASTER FX & PERF',
                    accent: PocketGridRetroPalette.studioTapeRed,
                    slice: [29, 32] as const,
                  },
                ].map((strip, sIdx) => {
                  const stripParams = TOP_32_PARAMS.slice(strip.slice[0], strip.slice[1]);
                  const isTwoColStrip = stripParams.length >= 8;
                  return (
                    <div
                      key={sIdx}
                      style={{
                        flex: isTwoColStrip ? 1.85 : 1,
                        minWidth: isTwoColStrip
                          ? isCompactViewport
                            ? '210px'
                            : '268px'
                          : isCompactViewport
                          ? '120px'
                          : '148px',
                        background: '#151619',
                        borderRadius: '6px',
                        border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                        padding: isCompactViewport ? '3px 5px' : '6px 8px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minHeight: 0,
                        overflowY: 'auto',
                      }}
                    >
                      <div
                        style={{
                          fontSize: isCompactViewport ? '8px' : '9.5px',
                          fontWeight: 800,
                          color: strip.accent,
                          borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                          paddingBottom: '2px',
                          marginBottom: '3px',
                          letterSpacing: '0.04em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {strip.title}
                      </div>
                      <div
                        style={
                          isTwoColStrip
                            ? {
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: isCompactViewport ? '3px 6px' : '6px 10px',
                                alignContent: 'space-around',
                                flex: 1,
                              }
                            : {
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-around',
                                gap: isCompactViewport ? '2px' : '4px',
                                flex: 1,
                              }
                        }
                      >
                        {stripParams.map((param) => {
                          const val = paramValues[param.id] ?? param.defaultValue;
                          return (
                            <PrecisionHorizontalSlider
                              key={param.id}
                              label={param.name}
                              formattedValue={param.formatDisplay(val)}
                              value={val}
                              defaultValue={param.defaultValue}
                              accentColor={strip.accent}
                              compact={isCompactViewport}
                              midiCcNumber={paramToCcMap[param.id] ?? param.defaultMidiCc}
                              isMidiLearning={learningParamId === param.id}
                              onToggleMidiLearn={() => toggleMidiLearnForParam(param.id)}
                              onChange={(v) => handleParamChange(param.id, v)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* View C: TAB 2 CHARACTER & RESONATORS (Deep Menu - CC 102 through 117) */}
            {mainControlSurfaceTab === 'CHARACTER_DEEP' && (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  gap: isCompactViewport ? '3px' : '8px',
                  minHeight: 0,
                  background: '#151619',
                  borderRadius: '6px',
                  border: `1px solid ${PocketGridRetroPalette.champagneBrass}66`,
                  padding: isCompactViewport ? '4px 8px' : '10px 16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                    paddingBottom: '3px',
                    marginBottom: '4px',
                  }}
                >
                  <span
                    style={{
                      fontSize: isCompactViewport ? '10px' : '11.5px',
                      fontWeight: 800,
                      color: PocketGridRetroPalette.champagneBrass,
                    }}
                  >
                    🔬 16 CHARACTER, ACOUSTIC RESONANCE & FORMANT SCULPTING PARAMETERS (CC 102 – 117)
                  </span>
                  <span style={{ fontSize: isCompactViewport ? '9px' : '10.5px', color: PocketGridRetroPalette.mutedPewterText }}>
                    Distinct MIDI CC mapping (CC 102–117) prevents collisions with primary faders
                  </span>
                </div>

                <div
                  style={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: isCompactViewport ? '6px 12px' : '12px 20px',
                    alignItems: 'center',
                    overflowY: 'auto',
                  }}
                >
                  {DEEP_CHARACTER_PARAMS.map((param, idx) => {
                    const val = paramValues[param.id] ?? param.defaultValue;
                    const accent =
                      idx < 4
                        ? PocketGridRetroPalette.champagneBrass
                        : idx < 8
                        ? PocketGridRetroPalette.tungstenAmber
                        : idx < 12
                        ? PocketGridRetroPalette.burntTerracotta
                        : PocketGridRetroPalette.electricTeal;
                    return (
                      <PrecisionHorizontalSlider
                        key={param.id}
                        label={param.name}
                        formattedValue={param.formatDisplay(val)}
                        value={val}
                        defaultValue={param.defaultValue}
                        accentColor={accent}
                        compact={isCompactViewport}
                        midiCcNumber={paramToCcMap[param.id] ?? param.defaultMidiCc}
                        isMidiLearning={learningParamId === param.id}
                        onToggleMidiLearn={() => toggleMidiLearnForParam(param.id)}
                        onChange={(v) => handleParamChange(param.id, v)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ----------------- RIGHT TRANSPORT, SMART CHORDS & INLINE DRUM RACK ----------------- */}
        <div
          style={{
            width: showDrumPads
              ? isCompactViewport
                ? '236px'
                : '340px'
              : isCompactViewport
              ? '126px'
              : '180px',
            minWidth: showDrumPads
              ? isCompactViewport
                ? '236px'
                : '340px'
              : isCompactViewport
              ? '126px'
              : '180px',
            maxWidth: showDrumPads
              ? isCompactViewport
                ? '244px'
                : '350px'
              : isCompactViewport
              ? '132px'
              : '188px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: isCompactViewport ? '2px' : '6px',
            flexShrink: 0,
            overflowY: 'auto',
            transition: 'width 120ms ease, min-width 120ms ease',
          }}
        >
          {/* [● REC] & [► PLAY] Hardware Buttons */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: isCompactViewport ? '2px' : '6px',
              height: isCompactViewport ? '24px' : '44px',
              flexShrink: 0,
            }}
          >
            <button
              onClick={async () => {
                await synthEngine.ensureInitialized();
                const nextRec = !isRecording;
                setIsRecording(nextRec);
                if (nextRec && !isPlaying) {
                  setIsPlaying(true);
                }
              }}
              style={{
                borderRadius: isCompactViewport ? '4px' : '6px',
                background: isRecording ? PocketGridRetroPalette.studioTapeRed : '#2B1F1E',
                border: `1.5px solid ${
                  isRecording ? '#FF8A80' : PocketGridRetroPalette.studioTapeRed
                }`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 800,
                fontSize: isCompactViewport ? '9.5px' : '12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isCompactViewport ? '1px' : '3px',
                boxShadow: isRecording ? '0 0 10px rgba(200,62,55,0.85)' : 'none',
              }}
            >
              <span
                style={{
                  width: isCompactViewport ? '6px' : '8px',
                  height: isCompactViewport ? '6px' : '8px',
                  borderRadius: '50%',
                  background: isRecording ? '#FFF3D6' : '#8C2D28',
                }}
              />
              REC
            </button>

            <button
              onClick={async () => {
                await synthEngine.ensureInitialized();
                setIsPlaying((p) => {
                  const next = !p;
                  if (!next) {
                    stopSequencerPlayback();
                  }
                  return next;
                });
              }}
              style={{
                borderRadius: isCompactViewport ? '4px' : '6px',
                background: isPlaying ? PocketGridRetroPalette.phosphorMoss : '#1E2822',
                border: `1.5px solid ${
                  isPlaying ? '#A5D6A7' : PocketGridRetroPalette.phosphorMoss
                }`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 800,
                fontSize: isCompactViewport ? '9.5px' : '12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isCompactViewport ? '1px' : '2px',
                boxShadow: isPlaying ? '0 0 10px rgba(78,154,103,0.85)' : 'none',
              }}
            >
              <span style={{ fontSize: isCompactViewport ? '9px' : '11px' }}>{isPlaying ? '■' : '►'}</span>
              {isPlaying ? 'STOP' : 'PLAY'}
            </button>
          </div>

          {/* [↶ UNDO], [HOLD], [⚡ FX] & [🥁 DRUMS] Row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              gap: isCompactViewport ? '2px' : '4px',
              height: isCompactViewport ? '18px' : '30px',
              flexShrink: 0,
            }}
          >
            <button
              onClick={handleUndo}
              style={{
                height: '100%',
                borderRadius: isCompactViewport ? '3px' : '5px',
                background: PocketGridRetroPalette.brushedTitanium,
                border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontWeight: 700,
                fontSize: isCompactViewport ? '7.5px' : '9.5px',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              ↶ UNDO
            </button>
            <button
              onClick={() => {
                const next = !isHoldActive;
                setIsHoldActive(next);
                synthEngine.setHoldPedal(next);
              }}
              style={{
                height: '100%',
                borderRadius: isCompactViewport ? '3px' : '5px',
                background: isHoldActive ? '#2C2318' : PocketGridRetroPalette.brushedTitanium,
                border: `1px solid ${
                  isHoldActive
                    ? PocketGridRetroPalette.tungstenAmber
                    : PocketGridRetroPalette.panelBorder
                }`,
                color: isHoldActive
                  ? PocketGridRetroPalette.tungstenAmber
                  : PocketGridRetroPalette.warmIvoryText,
                fontWeight: 700,
                fontSize: isCompactViewport ? '7.5px' : '9.5px',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              HOLD
            </button>
            <button
              onClick={() => {
                const next = !isLiveFxManualOpen;
                setIsLiveFxManualOpen(next);
                showToast(next ? 'Live FX Strip Visible' : 'Live FX Strip Hidden');
              }}
              title={isLiveFxVisible ? "Hide Live FX strip" : "Show Live FX strip (auto-shows on Play & Record)"}
              style={{
                height: '100%',
                borderRadius: isCompactViewport ? '3px' : '5px',
                background: isLiveFxVisible ? '#2D2316' : PocketGridRetroPalette.brushedTitanium,
                border: `1px solid ${
                  isLiveFxVisible
                    ? PocketGridRetroPalette.tungstenAmber
                    : PocketGridRetroPalette.panelBorder
                }`,
                color: isLiveFxVisible
                  ? PocketGridRetroPalette.tungstenAmber
                  : PocketGridRetroPalette.warmIvoryText,
                fontWeight: 800,
                fontSize: isCompactViewport ? '7.5px' : '9.5px',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1px',
              }}
            >
              <span>⚡</span>
              <span>FX</span>
            </button>
            <button
              onClick={() => setShowDrumPads((prev) => !prev)}
              title={showDrumPads ? 'Hide inline Drum Pads next to Chords' : 'Show inline 6-Pad Drum Rack next to Chords'}
              style={{
                height: '100%',
                borderRadius: isCompactViewport ? '3px' : '5px',
                background: showDrumPads
                  ? PocketGridRetroPalette.tungstenAmber
                  : PocketGridRetroPalette.brushedTitanium,
                border: `1px solid ${
                  showDrumPads
                    ? PocketGridRetroPalette.tungstenAmber
                    : PocketGridRetroPalette.panelBorder
                }`,
                color: showDrumPads ? '#141517' : PocketGridRetroPalette.warmIvoryText,
                fontWeight: 800,
                fontSize: isCompactViewport ? '7.5px' : '9.5px',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1px',
              }}
            >
              <span>🥁</span>
              <span>DRUM</span>
            </button>
          </div>

          {/* SIDE-BY-SIDE CHORDS (8) + INLINE DRUMS (6) RACK */}
          <div
            style={{
              display: 'flex',
              gap: isCompactViewport ? '4px' : '8px',
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* 8 SMART CHORDS RACK (2x4 Grid + EDIT Button) */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: isCompactViewport ? '1px' : '4px',
                  padding: '0 2px',
                }}
              >
                <span
                  style={{
                    fontSize: isCompactViewport ? '7.5px' : '10.5px',
                    fontWeight: 800,
                    color: PocketGridRetroPalette.champagneBrass,
                    letterSpacing: '0.04em',
                  }}
                >
                  CHORDS (10)
                </span>
                <button
                  onClick={() => setIsCustomChordModalOpen(true)}
                  title="Customize any of the 10 smart chords (all roots, voicings, qualities)"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: PocketGridRetroPalette.tungstenAmber,
                    fontSize: isCompactViewport ? '8px' : '10.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    padding: '1px 2px',
                  }}
                >
                  <span>⚙</span>
                  <span>EDIT</span>
                </button>
              </div>

              <div
                style={{
                  flex: 1,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gridTemplateRows: 'repeat(5, 1fr)',
                  gap: isCompactViewport ? '2px' : '6px',
                  minHeight: 0,
                  overflowY: 'auto',
                }}
              >
                {smartChords.map((chord, cIdx) => {
                  const isSelectedBrush =
                    seqLines[selectedSeqLineIdx]?.defaultChordLabel === chord.label;
                  return (
                    <button
                      key={`${chord.id}-${cIdx}`}
                      onPointerDown={() => handleChordTriggerDown(chord.label, chord.offsets)}
                      onPointerUp={() => handleChordTriggerUp(chord.offsets)}
                      onPointerLeave={() => handleChordTriggerUp(chord.offsets)}
                      onDoubleClick={() => setIsCustomChordModalOpen(true)}
                      title={`Tap to audition/paint ${chord.label}. Double-click to customize.`}
                      style={{
                        height: '100%',
                        minHeight: isCompactViewport ? '18px' : '32px',
                        borderRadius: isCompactViewport ? '3px' : '6px',
                        background: isSelectedBrush ? '#2E2418' : '#1C1D21',
                        border: `1.5px solid ${
                          isSelectedBrush
                            ? PocketGridRetroPalette.tungstenAmber
                            : PocketGridRetroPalette.panelBorder
                        }`,
                        color: isSelectedBrush
                          ? PocketGridRetroPalette.incandescentCream
                          : PocketGridRetroPalette.warmIvoryText,
                        fontWeight: 800,
                        fontSize: isCompactViewport
                          ? chord.label.length > 5
                            ? '7.5px'
                            : '8.5px'
                          : chord.label.length > 6
                          ? '11.5px'
                          : '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        padding: '0 4px',
                        boxShadow: isSelectedBrush
                          ? '0 0 8px rgba(217, 148, 59, 0.45)'
                          : 'none',
                      }}
                    >
                      {chord.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* INLINE 10-PAD STUDIO DRUM RACK (2x5 Grid directly next to Chords when turned on) */}
            {showDrumPads && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  borderLeft: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                  paddingLeft: isCompactViewport ? '4px' : '6px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: isCompactViewport ? '1px' : '4px',
                    padding: '0 2px',
                  }}
                >
                  <span
                    style={{
                      fontSize: isCompactViewport ? '7.5px' : '10.5px',
                      fontWeight: 800,
                      color: PocketGridRetroPalette.tungstenAmber,
                      letterSpacing: '0.04em',
                    }}
                  >
                    🥁 DRUMS (10)
                  </span>
                  <button
                    onClick={() => setShowDrumPads(false)}
                    title="Hide inline Drum Pads"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: PocketGridRetroPalette.mutedPewterText,
                      fontSize: isCompactViewport ? '8px' : '10px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      padding: '0 2px',
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div
                  style={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gridTemplateRows: 'repeat(5, 1fr)',
                    gap: isCompactViewport ? '2px' : '6px',
                    minHeight: 0,
                  }}
                >
                  {DRUM_LABELS.map((label, dIdx) => {
                    const isHit = activeDrumPad === dIdx;
                    return (
                      <button
                        key={label}
                        onPointerDown={async () => {
                          setActiveDrumPad(dIdx);
                          await synthEngine.ensureInitialized();
                          void synthEngine.triggerDrum(dIdx, 0.95);
                          if (isRecording && isSeqModeActive) {
                            pushUndoSnapshot();
                            const targetLineIdx = dIdx < 4 ? dIdx : 3;
                            setSeqLines((prev) =>
                              prev.map((l, lIdx) =>
                                lIdx === targetLineIdx
                                  ? {
                                      ...l,
                                      kind: 'drum',
                                      drumIdx: dIdx,
                                      steps: l.steps.map((st, sIdx) =>
                                        sIdx === activeStepIdx ? { ...st, active: true } : st
                                      ),
                                    }
                                  : l
                              )
                            );
                          }
                        }}
                        onPointerUp={() => setActiveDrumPad(null)}
                        onPointerLeave={() => setActiveDrumPad(null)}
                        onPointerCancel={() => setActiveDrumPad(null)}
                        title={`Trigger ${label} drum voice`}
                        style={{
                          height: '100%',
                          minHeight: isCompactViewport ? '18px' : '32px',
                          borderRadius: isCompactViewport ? '3px' : '6px',
                          background: isHit ? PocketGridRetroPalette.tungstenAmber : '#1F1B18',
                          border: `1.5px solid ${
                            isHit ? '#FFF3D6' : PocketGridRetroPalette.tungstenAmber
                          }`,
                          color: isHit ? '#141517' : PocketGridRetroPalette.warmIvoryText,
                          fontWeight: 800,
                          fontSize: isCompactViewport ? '7.5px' : '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 2px',
                          touchAction: 'none',
                          userSelect: 'none',
                          boxShadow: isHit
                            ? '0 0 10px rgba(217, 148, 59, 0.85)'
                            : `inset 0 0 8px ${PocketGridRetroPalette.tungstenAmber}22`,
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* =====================================================================
          3. SAVED SEQUENCE BANK BAR BELOW THE SEQUENCER (8 SLOTS + SAVE SEQUENCE)
             (Only shown when in SEQ_8_LINES mode to save vertical screen space in SLIDERS mode)
         ===================================================================== */}
      {deckMode === 'SEQ_8_LINES' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '6px',
            padding: '4px 12px',
            background: '#191A1E',
            borderTop: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, minWidth: 0 }}>
            <span
              style={{
                fontSize: '10px',
                fontWeight: 800,
                color: PocketGridRetroPalette.tungstenAmber,
                whiteSpace: 'nowrap',
                marginRight: '4px',
              }}
            >
              🗂 SEQUENCE BANK:
            </span>

            {sequenceBank.map((slot) => {
              const isActiveSlot = activeSeqBankSlot === slot.slotIndex;
              return (
                <button
                  key={slot.slotIndex}
                  onClick={() => handleLoadSequenceFromBank(slot)}
                  title={`Click to load Sequence Slot ${slot.slotIndex} (${slot.stepCount} steps @ ${slot.bpm} BPM)`}
                  style={{
                    flex: 1,
                    minWidth: '72px',
                    height: '24px',
                    padding: '0 6px',
                    borderRadius: '4px',
                    background: isActiveSlot
                      ? '#2D2418'
                      : slot.isPopulated
                      ? PocketGridRetroPalette.brushedTitanium
                      : '#141518',
                    border: `1px ${slot.isPopulated ? 'solid' : 'dashed'} ${
                      isActiveSlot
                        ? PocketGridRetroPalette.tungstenAmber
                        : slot.isPopulated
                        ? PocketGridRetroPalette.vintageVfdSage
                        : PocketGridRetroPalette.panelBorder
                    }`,
                    color: isActiveSlot
                      ? PocketGridRetroPalette.incandescentCream
                      : slot.isPopulated
                      ? PocketGridRetroPalette.warmIvoryText
                      : PocketGridRetroPalette.mutedPewterText,
                    fontSize: '9px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {slot.name}
                </button>
              );
            })}
          </div>

          {/* Save Current Sequence Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="text"
              value={seqCustomTitle}
              onChange={(e) => setSeqCustomTitle(e.target.value)}
              placeholder="Sequence name..."
              style={{
                width: '115px',
                height: '22px',
                padding: '0 6px',
                borderRadius: '4px',
                background: '#121315',
                border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontSize: '10px',
                fontWeight: 600,
              }}
            />
            <select
              value={seqSaveSlotTarget}
              onChange={(e) => setSeqSaveSlotTarget(Number(e.target.value))}
              style={{
                height: '24px',
                background: '#121315',
                color: PocketGridRetroPalette.warmIvoryText,
                border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 700,
                padding: '0 4px',
              }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>
                  SLOT {n}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleSaveSequenceToBank(seqSaveSlotTarget)}
              style={{
                height: '24px',
                padding: '0 9px',
                borderRadius: '4px',
                background: PocketGridRetroPalette.phosphorMoss,
                border: `1px solid ${PocketGridRetroPalette.incandescentCream}`,
                color: PocketGridRetroPalette.incandescentCream,
                fontSize: '10px',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              💾 SAVE SEQUENCE
            </button>
            <button
              onClick={() => setIsSaveSeqModalOpen(true)}
              title="Open Sequence Bank & Slots Manager"
              style={{
                height: '24px',
                padding: '0 8px',
                borderRadius: '4px',
                background: '#242018',
                border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                color: PocketGridRetroPalette.tungstenAmber,
                fontSize: '10px',
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              🗂 BANK SLOTS
            </button>
          </div>
        </div>
      )}

      {/* =====================================================================
          3.5 LIVE PERFORMANCE FX STRIP (DJ Filter, Stutter, Reverse, Gate, Pump, Bend, Tape Stop)
              (Optional: Hidden when stopped to save screen real estate, auto-appears during PLAY or RECORD)
         ===================================================================== */}
      {isLiveFxVisible && (
        <div
          style={{
            height: isCompactViewport ? '32px' : '54px',
            minHeight: isCompactViewport ? '32px' : '54px',
            maxHeight: isCompactViewport ? '32px' : '54px',
            padding: isCompactViewport ? '1px 6px' : '4px 12px',
            background: '#131417',
            borderTop: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
            display: 'flex',
            alignItems: 'center',
            gap: isCompactViewport ? '5px' : '10px',
            boxSizing: 'border-box',
            flexShrink: 0,
            zIndex: 10,
          }}
        >
          {/* Left Label & FX HOLD Toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isCompactViewport ? '3px' : '6px',
              minWidth: isCompactViewport ? '115px' : '150px',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                fontSize: isCompactViewport ? '9px' : '11px',
                fontWeight: 800,
                color: PocketGridRetroPalette.incandescentCream,
                letterSpacing: '0.04em',
              }}
            >
              🎛 LIVE FX:
            </span>
            <button
              onClick={() => {
                const next = !isFxHoldOn;
                setIsFxHoldOn(next);
                showToast(
                  next
                    ? 'FX HOLD ON: Performance faders latch at current positions'
                    : 'FX HOLD OFF: Performance faders spring back to neutral on release'
                );
              }}
              title="Latch faders in position (ON) or spring-back to neutral when released (OFF)"
              style={{
                padding: isCompactViewport ? '1px 5px' : '4px 8px',
                borderRadius: '4px',
                background: isFxHoldOn ? PocketGridRetroPalette.studioTapeRed : '#241D1D',
                border: `1.5px solid ${
                  isFxHoldOn ? '#FFA49E' : PocketGridRetroPalette.studioTapeRed
                }`,
                color: PocketGridRetroPalette.warmIvoryText,
                fontSize: isCompactViewport ? '8px' : '10px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: isFxHoldOn ? '0 0 8px rgba(200,62,55,0.7)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              ⚡ HOLD: {isFxHoldOn ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* 7 Performance FX Sliders */}
          <div
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: isCompactViewport ? '4px' : '10px',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            {PERFORMANCE_FX_PARAMS.map((param) => {
              const val = performanceFxValues[param.id] ?? param.defaultValue;
              return (
                <PrecisionHorizontalSlider
                  key={param.id}
                  label={param.shortLabel}
                  formattedValue={param.formatDisplay(val)}
                  value={val}
                  defaultValue={param.defaultValue}
                  accentColor={param.color}
                  compact={isCompactViewport}
                  midiCcNumber={paramToCcMap[param.id] ?? param.defaultMidiCc}
                  bipolar={param.bipolar}
                  springBack={param.springBack}
                  springBackTarget={param.springBackTarget}
                  holdActive={isFxHoldOn}
                  isMidiLearning={learningParamId === param.id}
                  onToggleMidiLearn={() => toggleMidiLearnForParam(param.id)}
                  onChange={(v) => handlePerfFxChange(param.id, v)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* =====================================================================
          4. BOTTOM DECK: 5-KEY AUDITION BAR (DEFAULT) OR EXPANDED 24-KEY KEYBED
             (Gives about 5 tactile keys when picking & editing sounds to free up screen real estate)
         ===================================================================== */}
      {keybedLayoutMode === '5_AUDITION_KEYS' ? (
        <div
          style={{
            height: isCompactViewport ? '38px' : '66px',
            minHeight: isCompactViewport ? '38px' : '66px',
            maxHeight: isCompactViewport ? '38px' : '66px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#16171A',
            borderTop: `1.5px solid ${PocketGridRetroPalette.panelBorder}`,
            padding: isCompactViewport ? '1px 6px' : '4px 12px',
            gap: isCompactViewport ? '6px' : '12px',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          {/* Left: Active/Split Sound Indicators + [🔍] + [🔧 Fine Tune] */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isCompactViewport ? '3px' : '6px', minWidth: 0, flexShrink: 0 }}>
            {isSplitEnabled ? (
              <>
                <button
                  onClick={() => {
                    setSplitSoundPickerTarget('LOWER');
                    setIsFindSoundsOpen(true);
                  }}
                  title={`Lower Zone Preset: ${lowerZonePreset.name}. Click to pick another sound.`}
                  style={{
                    height: isCompactViewport ? '28px' : '40px',
                    padding: isCompactViewport ? '0 5px' : '0 10px',
                    borderRadius: isCompactViewport ? '4px' : '6px',
                    background: '#20180F',
                    border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                    color: PocketGridRetroPalette.tungstenAmber,
                    fontWeight: 800,
                    fontSize: isCompactViewport ? '8.5px' : '11px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: isCompactViewport ? '100px' : '160px',
                  }}
                >
                  🔍 LOW: {lowerZonePreset.name.replace(/^\d+\.\s*/, '')}
                </button>

                <button
                  onClick={() => {
                    setSplitSoundPickerTarget('UPPER');
                    setIsFindSoundsOpen(true);
                  }}
                  title={`Upper Zone Preset: ${upperZonePreset.name}. Click to pick another sound.`}
                  style={{
                    height: isCompactViewport ? '28px' : '40px',
                    padding: isCompactViewport ? '0 5px' : '0 10px',
                    borderRadius: isCompactViewport ? '4px' : '6px',
                    background: '#161F1B',
                    border: `1.5px solid ${PocketGridRetroPalette.vintageVfdSage}`,
                    color: PocketGridRetroPalette.vintageVfdSage,
                    fontWeight: 800,
                    fontSize: isCompactViewport ? '8.5px' : '11px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: isCompactViewport ? '100px' : '160px',
                  }}
                >
                  🔍 UPP: {upperZonePreset.name.replace(/^\d+\.\s*/, '')}
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setSplitSoundPickerTarget('ACTIVE');
                  setIsFindSoundsOpen(true);
                }}
                title={`Active Preset: ${activePreset.name}. Click to pick another sound.`}
                style={{
                  height: isCompactViewport ? '28px' : '40px',
                  padding: isCompactViewport ? '0 7px' : '0 12px',
                  borderRadius: isCompactViewport ? '4px' : '6px',
                  background: '#1A1813',
                  border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                  color: PocketGridRetroPalette.tungstenAmber,
                  fontWeight: 800,
                  fontSize: isCompactViewport ? '9.5px' : '12px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isCompactViewport ? '140px' : '240px',
                }}
              >
                🔍 {activePreset.name}
              </button>
            )}

            <button
              onClick={() =>
                setFineTuneTarget({
                  soundId: isSplitEnabled ? lowerZonePreset.id : activePreset.id,
                  soundName: isSplitEnabled ? lowerZonePreset.name : activePreset.name,
                  sectionLabel: isSplitEnabled ? 'LOWER ZONE SOUND' : 'ACTIVE SOUND',
                })
              }
              title="Fine Tune sound parameters (cents, velocity curve, acoustic warmth)"
              style={{
                height: isCompactViewport ? '28px' : '40px',
                padding: isCompactViewport ? '0 5px' : '0 10px',
                borderRadius: '5px',
                background: '#1E2328',
                border: `1.5px solid ${PocketGridRetroPalette.electricTeal}`,
                color: PocketGridRetroPalette.electricTeal,
                fontWeight: 800,
                fontSize: isCompactViewport ? '9px' : '11.5px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              🔧 Tune
            </button>

            {/* Octave Shifter */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: '#141517',
                border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                borderRadius: isCompactViewport ? '4px' : '6px',
                height: isCompactViewport ? '30px' : '40px',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => handleGlobalOctaveChange(-1)}
                title="Shift down 1 octave"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: PocketGridRetroPalette.warmIvoryText,
                  padding: isCompactViewport ? '0 6px' : '0 10px',
                  height: '100%',
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: isCompactViewport ? '12px' : '15px',
                }}
              >
                −
              </button>
              <span
                style={{
                  fontSize: isCompactViewport ? '10px' : '12px',
                  fontWeight: 800,
                  padding: isCompactViewport ? '0 4px' : '0 8px',
                  color:
                    globalOctave !== 0
                      ? PocketGridRetroPalette.tungstenAmber
                      : PocketGridRetroPalette.warmIvoryText,
                }}
              >
                OCT {globalOctave >= 0 ? `+${globalOctave}` : globalOctave}
              </span>
              <button
                onClick={() => handleGlobalOctaveChange(1)}
                title="Shift up 1 octave"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: PocketGridRetroPalette.warmIvoryText,
                  padding: isCompactViewport ? '0 6px' : '0 10px',
                  height: '100%',
                  cursor: 'pointer',
                  fontWeight: 800,
                  fontSize: isCompactViewport ? '12px' : '15px',
                }}
              >
                +
              </button>
            </div>
          </div>

          {/* Center: 5 Large Audition Keys */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isCompactViewport ? '4px' : '8px',
              flex: 1,
              justifyContent: 'center',
              maxWidth: isCompactViewport ? '380px' : '640px',
              height: '100%',
            }}
          >
            {[
              { semitone: 0, label: 'ROOT' },
              { semitone: 2, label: '2ND' },
              { semitone: 4, label: '3RD' },
              { semitone: 7, label: '5TH' },
              { semitone: 12, label: 'OCT' },
            ].map((k, idx) => {
              const isLowerAudition = isSplitEnabled && idx < 2;
              const rawMidi = (isLowerAudition ? 36 : 48) + k.semitone;
              const octOffset = isSplitEnabled
                ? isLowerAudition
                  ? lowerZoneOctave * 12
                  : upperZoneOctave * 12
                : globalOctave * 12;
              const naturalMidi = rawMidi + octOffset;
              const rootMidiOffset = ROOT_SEMITONES[rootKey] ?? 2;
              const finalMidi = isScaleSnapEnabled
                ? quantizeNoteToScale(naturalMidi, rootMidiOffset, currentScale)
                : naturalMidi;
              const noteName = formatMidiNote(finalMidi);
              const isPressed = Boolean(activeKeys[rawMidi]);

              return (
                <button
                  key={idx}
                  onPointerDown={(e) => {
                    try {
                      e.currentTarget.setPointerCapture(e.pointerId);
                    } catch {}
                    handleKeyPointerDown(e, rawMidi, isLowerAudition);
                  }}
                  onPointerUp={(e) => {
                    try {
                      e.currentTarget.releasePointerCapture(e.pointerId);
                    } catch {}
                    handleKeyPointerUp(rawMidi, isLowerAudition);
                  }}
                  onPointerCancel={() => handleKeyPointerUp(rawMidi, isLowerAudition)}
                  onPointerLeave={() => handleKeyPointerUp(rawMidi, isLowerAudition)}
                  title={
                    isSplitEnabled
                      ? isLowerAudition
                        ? `Audition Lower Sound: ${lowerZonePreset.name}`
                        : `Audition Upper Sound: ${upperZonePreset.name}`
                      : `Audition Active Sound: ${activePreset.name}`
                  }
                  style={{
                    flex: 1,
                    height: isCompactViewport ? '30px' : '50px',
                    borderRadius: isCompactViewport ? '4px' : '6px',
                    background: isPressed
                      ? PocketGridRetroPalette.tungstenAmber
                      : isLowerAudition
                      ? '#2A2016'
                      : PocketGridRetroPalette.warmIvoryText,
                    border: `1.5px solid ${
                      isPressed
                        ? '#FFF'
                        : isLowerAudition
                        ? PocketGridRetroPalette.tungstenAmber
                        : '#333'
                    }`,
                    color: isPressed
                      ? '#141517'
                      : isLowerAudition
                      ? PocketGridRetroPalette.tungstenAmber
                      : '#1E1F22',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    touchAction: 'none',
                    boxShadow: isPressed
                      ? '0 0 12px rgba(217, 148, 59, 0.9)'
                      : 'inset 0 -3px 5px rgba(0,0,0,0.28)',
                  }}
                >
                  <span style={{ fontSize: isCompactViewport ? '10.5px' : '14px', fontWeight: 900, lineHeight: 1 }}>
                    {noteName}
                  </span>
                  <span
                    style={{
                      fontSize: isCompactViewport ? '6.5px' : '8.5px',
                      fontWeight: 800,
                      opacity: 0.85,
                      lineHeight: 1,
                      marginTop: isCompactViewport ? '1px' : '3px',
                    }}
                  >
                    {isSplitEnabled ? (isLowerAudition ? `LOW • ${k.label}` : `UPP • ${k.label}`) : k.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Right: Expand to Full 24-Key Keyboard */}
          <button
            onClick={() => setKeybedLayoutMode('FULL_24_KEYS')}
            title="Expand to Full 24-Key Two-Octave Keyboard for performing"
            style={{
              height: isCompactViewport ? '28px' : '40px',
              padding: isCompactViewport ? '0 7px' : '0 14px',
              borderRadius: isCompactViewport ? '4px' : '6px',
              background: '#232529',
              border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
              color: PocketGridRetroPalette.warmIvoryText,
              fontWeight: 800,
              fontSize: isCompactViewport ? '9.5px' : '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <span>🎹 FULL 24 KEYS</span>
            <span style={{ fontSize: isCompactViewport ? '11px' : '14px' }}>⤢</span>
          </button>
        </div>
      ) : (
        <div
          style={{
            height: isPortrait ? '96px' : isCompactViewport ? '68px' : '180px',
            minHeight: isPortrait ? '90px' : isCompactViewport ? '64px' : '160px',
            maxHeight: isPortrait ? '104px' : isCompactViewport ? '74px' : '200px',
            display: 'flex',
            flexDirection: 'column',
            background: '#16171A',
            borderTop: `1.5px solid ${PocketGridRetroPalette.panelBorder}`,
            flexShrink: 0,
          }}
        >
          {/* Keybed Header Bar: Independent Sound Preset Selectors + Mini Octave Controls + Split Toggle */}
          <div
            style={{
              minHeight: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 6px',
              background: '#1F2024',
              borderBottom: `1px solid ${PocketGridRetroPalette.panelBorder}`,
              fontSize: '9.5px',
              fontWeight: 700,
              gap: '4px',
            }}
          >
          {isSplitEnabled ? (
            <>
              {/* LOWER ZONE 1: Sound Selector + Octave Controls + Latch */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* Lower Octave Buttons */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    background: '#161719',
                    border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <button
                    onClick={() => handleLowerOctaveChange(-1)}
                    title="Lower Zone 1: Transpose down 1 octave"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: PocketGridRetroPalette.warmIvoryText,
                      padding: '2px 6px',
                      cursor: 'pointer',
                      fontWeight: 800,
                    }}
                  >
                    −
                  </button>
                  <span
                    style={{
                      padding: '0 5px',
                      fontWeight: 800,
                      color:
                        lowerZoneOctave !== 0
                          ? PocketGridRetroPalette.tungstenAmber
                          : PocketGridRetroPalette.warmIvoryText,
                    }}
                  >
                    OCT {lowerZoneOctave >= 0 ? `+${lowerZoneOctave}` : lowerZoneOctave}
                  </span>
                  <button
                    onClick={() => handleLowerOctaveChange(1)}
                    title="Lower Zone 1: Transpose up 1 octave"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: PocketGridRetroPalette.warmIvoryText,
                      padding: '2px 6px',
                      cursor: 'pointer',
                      fontWeight: 800,
                    }}
                  >
                    +
                  </button>
                </div>

                {/* Lower Zone Sound Dropdown */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span
                    style={{
                      color: PocketGridRetroPalette.tungstenAmber,
                      fontSize: '9px',
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                    }}
                  >
                    LOWER:
                  </span>
                  <select
                    value={lowerZonePreset.id}
                    onChange={(e) => {
                      const found = presets.find((p) => p.id === e.target.value);
                      if (found) {
                        setLowerZonePreset(found);
                        lowerZonePresetRef.current = found;
                        void synthEngine.setZonePreset(0, found);
                        const auditionNote = 48 + lowerZoneOctave * 12;
                        void synthEngine.noteOn(
                          auditionNote,
                          0.85,
                          9876,
                          0,
                          found.targetEngineId,
                          found.opRatios,
                          found.opLevels,
                          found.params
                        );
                        window.setTimeout(() => synthEngine.noteOff(auditionNote, 9876), 220);
                        showToast(`Lower Split Zone: ${found.name}`);
                      }
                    }}
                    title="Select instrument sound for Lower Zone (left half of split keyboard)"
                    style={{
                      height: '22px',
                      maxWidth: '140px',
                      background: '#1A1813',
                      color: PocketGridRetroPalette.tungstenAmber,
                      border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                      borderRadius: '4px',
                      fontSize: '9px',
                      fontWeight: 800,
                      padding: '0 4px',
                      cursor: 'pointer',
                    }}
                  >
                    {INSTRUMENT_CATEGORIES.map((cat) => (
                      <optgroup key={cat.id} label={`${cat.icon} ${cat.label}`}>
                        {presets
                          .filter((p) => p.category === cat.id)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      setSplitSoundPickerTarget('LOWER');
                      setIsFindSoundsOpen(true);
                    }}
                    title="Browse & customize sounds for Lower Split Zone"
                    style={{
                      height: '22px',
                      padding: '0 5px',
                      background: '#1A1813',
                      color: PocketGridRetroPalette.tungstenAmber,
                      border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                      borderRadius: '4px',
                      fontSize: '10px',
                      cursor: 'pointer',
                      fontWeight: 800,
                    }}
                  >
                    🔍
                  </button>
                  <button
                    onClick={() =>
                      setFineTuneTarget({
                        soundId: lowerZonePreset.id,
                        soundName: lowerZonePreset.name,
                        sectionLabel: 'LOWER SPLIT ZONE',
                      })
                    }
                    title="Fine Tune sound parameters (pitch cents, velocity dynamics, acoustic warmth)"
                    style={{
                      height: '22px',
                      padding: '0 6px',
                      background: '#201A12',
                      color: PocketGridRetroPalette.tungstenAmber,
                      border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                      borderRadius: '4px',
                      fontSize: '10px',
                      cursor: 'pointer',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                    }}
                  >
                    🔧 Fine Tune
                  </button>
                </div>

                {/* Lower Zone Latch Button */}
                <button
                  onClick={() => {
                    const nextLatch = !isLowerLatch;
                    setIsLowerLatch(nextLatch);
                    if (!nextLatch && latchedLowerNote !== null) {
                      const natural = latchedLowerNote + lowerZoneOctave * 12;
                      const rootMidiOffset = ROOT_SEMITONES[rootKey] ?? 2;
                      const finalN = isScaleSnapEnabled
                        ? quantizeNoteToScale(natural, rootMidiOffset, currentScale)
                        : natural;
                      synthEngine.noteOff(finalN, 100 + latchedLowerNote, 0);
                      setLatchedLowerNote(null);
                    }
                  }}
                  title="Latch/Drone the last played note in Lower Zone"
                  style={{
                    padding: '2px 6px',
                    borderRadius: '3px',
                    background: isLowerLatch ? PocketGridRetroPalette.tungstenAmber : '#232529',
                    color: isLowerLatch ? '#1E1F22' : PocketGridRetroPalette.mutedPewterText,
                    border: `1px solid ${
                      isLowerLatch ? PocketGridRetroPalette.tungstenAmber : PocketGridRetroPalette.panelBorder
                    }`,
                    fontSize: '9px',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  LATCH {isLowerLatch ? 'ON' : 'OFF'}
                </button>
              </div>

              {/* CENTER: Split Mode Toggle Button */}
              <button
                onClick={() => setIsSplitEnabled(false)}
                title="Switch to Full 24-Key Keyboard mode (uses Main Sound across all keys)"
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  background: '#27292E',
                  border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                  color: PocketGridRetroPalette.warmIvoryText,
                  fontWeight: 800,
                  fontSize: '9px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                [SPLIT: C4 | ⇄ FULL KEYBOARD]
              </button>

              {/* UPPER ZONE 2: Sound Selector + Octave Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* Upper Zone Sound Dropdown */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span
                    style={{
                      color: PocketGridRetroPalette.vintageVfdSage,
                      fontSize: '9px',
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                    }}
                  >
                    UPPER:
                  </span>
                  <select
                    value={upperZonePreset.id}
                    onChange={(e) => {
                      const found = presets.find((p) => p.id === e.target.value);
                      if (found) {
                        setUpperZonePreset(found);
                        upperZonePresetRef.current = found;
                        setActivePreset(found);
                        void synthEngine.loadPreset(found);
                        void synthEngine.setZonePreset(1, found);
                        setParamValues(expandPresetParamsToAllViews(found.params));
                        const auditionNote = 60 + upperZoneOctave * 12;
                        void synthEngine.noteOn(
                          auditionNote,
                          0.85,
                          9877,
                          1,
                          found.targetEngineId,
                          found.opRatios,
                          found.opLevels,
                          found.params
                        );
                        window.setTimeout(() => synthEngine.noteOff(auditionNote, 9877), 220);
                        showToast(`Upper Split Zone: ${found.name}`);
                      }
                    }}
                    title="Select instrument sound for Upper Zone (right half of split keyboard)"
                    style={{
                      height: '22px',
                      maxWidth: '140px',
                      background: '#131A17',
                      color: PocketGridRetroPalette.vintageVfdSage,
                      border: `1.5px solid ${PocketGridRetroPalette.vintageVfdSage}`,
                      borderRadius: '4px',
                      fontSize: '9px',
                      fontWeight: 800,
                      padding: '0 4px',
                      cursor: 'pointer',
                    }}
                  >
                    {INSTRUMENT_CATEGORIES.map((cat) => (
                      <optgroup key={cat.id} label={`${cat.icon} ${cat.label}`}>
                        {presets
                          .filter((p) => p.category === cat.id)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      setSplitSoundPickerTarget('UPPER');
                      setIsFindSoundsOpen(true);
                    }}
                    title="Browse & customize sounds for Upper Split Zone"
                    style={{
                      height: '22px',
                      padding: '0 5px',
                      background: '#131A17',
                      color: PocketGridRetroPalette.vintageVfdSage,
                      border: `1.5px solid ${PocketGridRetroPalette.vintageVfdSage}`,
                      borderRadius: '4px',
                      fontSize: '10px',
                      cursor: 'pointer',
                      fontWeight: 800,
                    }}
                  >
                    🔍
                  </button>
                  <button
                    onClick={() =>
                      setFineTuneTarget({
                        soundId: upperZonePreset.id,
                        soundName: upperZonePreset.name,
                        sectionLabel: 'UPPER SPLIT ZONE',
                      })
                    }
                    title="Fine Tune sound parameters (pitch cents, velocity dynamics, acoustic warmth)"
                    style={{
                      height: '22px',
                      padding: '0 6px',
                      background: '#131A17',
                      color: PocketGridRetroPalette.vintageVfdSage,
                      border: `1.5px solid ${PocketGridRetroPalette.vintageVfdSage}`,
                      borderRadius: '4px',
                      fontSize: '10px',
                      cursor: 'pointer',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                    }}
                  >
                    🔧 Fine Tune
                  </button>
                </div>

                {/* Upper Octave Buttons */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    background: '#161719',
                    border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <button
                    onClick={() => handleUpperOctaveChange(-1)}
                    title="Upper Zone 2: Transpose down 1 octave"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: PocketGridRetroPalette.warmIvoryText,
                      padding: '2px 6px',
                      cursor: 'pointer',
                      fontWeight: 800,
                    }}
                  >
                    −
                  </button>
                  <span
                    style={{
                      padding: '0 5px',
                      fontWeight: 800,
                      color:
                        upperZoneOctave !== 0
                          ? PocketGridRetroPalette.vintageVfdSage
                          : PocketGridRetroPalette.warmIvoryText,
                    }}
                  >
                    OCT {upperZoneOctave >= 0 ? `+${upperZoneOctave}` : upperZoneOctave}
                  </span>
                  <button
                    onClick={() => handleUpperOctaveChange(1)}
                    title="Upper Zone 2: Transpose up 1 octave"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: PocketGridRetroPalette.warmIvoryText,
                      padding: '2px 6px',
                      cursor: 'pointer',
                      fontWeight: 800,
                    }}
                  >
                    +
                  </button>
                </div>

                {/* Collapse to 5-Key Audition Keybed Button */}
                <button
                  onClick={() => setKeybedLayoutMode('5_AUDITION_KEYS')}
                  title="Collapse to 5-Key Audition bar for more screen space"
                  style={{
                    height: '22px',
                    padding: '0 7px',
                    borderRadius: '4px',
                    background: '#2A2016',
                    border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                    color: PocketGridRetroPalette.tungstenAmber,
                    fontWeight: 800,
                    fontSize: '9.5px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span>🎹 5 AUDITION KEYS</span>
                  <span style={{ fontSize: '11px' }}>⤡</span>
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Full Keyboard Mode Sound Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: PocketGridRetroPalette.warmIvoryText, fontWeight: 800, fontSize: '9px' }}>
                  SOUND:
                </span>
                <select
                  value={activePreset.id}
                  onChange={(e) => {
                    const found = presets.find((p) => p.id === e.target.value);
                    if (found) {
                      setActivePreset(found);
                      setUpperZonePreset(found);
                      upperZonePresetRef.current = found;
                      void synthEngine.loadPreset(found);
                      void synthEngine.setZonePreset(1, found);
                      setParamValues(expandPresetParamsToAllViews(found.params));
                      const auditionNote = 60 + globalOctave * 12;
                      void synthEngine.noteOn(
                        auditionNote,
                        0.85,
                        9878,
                        1,
                        found.targetEngineId,
                        found.opRatios,
                        found.opLevels,
                        found.params
                      );
                      window.setTimeout(() => synthEngine.noteOff(auditionNote, 9878), 220);
                      showToast(`Main Preset: ${found.name}`);
                    }
                  }}
                  style={{
                    height: '22px',
                    maxWidth: '220px',
                    background: '#161719',
                    color: PocketGridRetroPalette.warmIvoryText,
                    border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: 800,
                    padding: '0 4px',
                    cursor: 'pointer',
                  }}
                >
                  {INSTRUMENT_CATEGORIES.map((cat) => (
                    <optgroup key={cat.id} label={`${cat.icon} ${cat.label}`}>
                      {presets
                        .filter((p) => p.category === cat.id)
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </select>
                <button
                  onClick={() => {
                    setSplitSoundPickerTarget('ACTIVE');
                    setIsFindSoundsOpen(true);
                  }}
                  title="Browse & customize sounds for Main Keyboard"
                  style={{
                    height: '22px',
                    padding: '0 5px',
                    background: '#1E2328',
                    color: PocketGridRetroPalette.electricTeal,
                    border: `1.5px solid ${PocketGridRetroPalette.electricTeal}`,
                    borderRadius: '4px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    fontWeight: 800,
                  }}
                >
                  🔍
                </button>
                <button
                  onClick={() =>
                    setFineTuneTarget({
                      soundId: activePreset.id,
                      soundName: activePreset.name,
                      sectionLabel: 'MAIN SOUND',
                    })
                  }
                  title="Fine Tune sound parameters (pitch cents, velocity dynamics, acoustic warmth)"
                  style={{
                    height: '22px',
                    padding: '0 6px',
                    background: '#1E2328',
                    color: PocketGridRetroPalette.electricTeal,
                    border: `1.5px solid ${PocketGridRetroPalette.electricTeal}`,
                    borderRadius: '4px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                  }}
                >
                  🔧 Fine Tune
                </button>
                <span style={{ color: PocketGridRetroPalette.mutedPewterText, fontSize: '9px' }}>
                  (MPE X/Y/Z ACTIVE)
                </span>
              </div>

              {/* Enable Split Button */}
              <button
                onClick={() => setIsSplitEnabled(true)}
                title="Split Keyboard into Lower Bass Zone and Upper Lead/Keys Zone"
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  background: '#2C2318',
                  border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                  color: PocketGridRetroPalette.tungstenAmber,
                  fontWeight: 800,
                  fontSize: '9px',
                  cursor: 'pointer',
                }}
              >
                [FULL KEYBOARD | ⇄ ENABLE SPLIT: C4]
              </button>

              {/* Global Octave Buttons */}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: '#161719',
                  border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => handleGlobalOctaveChange(-1)}
                  title="Transpose full keyboard down 1 octave"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: PocketGridRetroPalette.warmIvoryText,
                    padding: '2px 7px',
                    cursor: 'pointer',
                    fontWeight: 800,
                  }}
                >
                  −
                </button>
                <span
                  style={{
                    padding: '0 6px',
                    fontWeight: 800,
                    color:
                      globalOctave !== 0
                        ? PocketGridRetroPalette.tungstenAmber
                        : PocketGridRetroPalette.warmIvoryText,
                  }}
                >
                  OCT {globalOctave >= 0 ? `+${globalOctave}` : globalOctave}
                </span>
                <button
                  onClick={() => handleGlobalOctaveChange(1)}
                  title="Transpose full keyboard up 1 octave"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: PocketGridRetroPalette.warmIvoryText,
                    padding: '2px 7px',
                    cursor: 'pointer',
                    fontWeight: 800,
                  }}
                >
                  +
                </button>
              </div>

              {/* Collapse to 5-Key Audition Keybed Button */}
              <button
                onClick={() => setKeybedLayoutMode('5_AUDITION_KEYS')}
                title="Collapse to 5-Key Audition bar for more screen space"
                style={{
                  height: '24px',
                  padding: '0 8px',
                  borderRadius: '4px',
                  background: '#2A2016',
                  border: `1.5px solid ${PocketGridRetroPalette.tungstenAmber}`,
                  color: PocketGridRetroPalette.tungstenAmber,
                  fontWeight: 800,
                  fontSize: '9.5px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>🎹 5 AUDITION KEYS</span>
                <span style={{ fontSize: '11px' }}>⤡</span>
              </button>
            </>
          )}
        </div>

        {/* Expressive Multi-Touch Keybed Surface (Standard 24-Key: C3 to B4 with Dynamic Note Labels) */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            padding: '3px 8px 6px 8px',
            gap: '2px',
            background: '#121315',
            touchAction: 'none',
          }}
        >
          {keybedBaseNotes.map((rawNote, idx) => {
            const isLower = isSplitEnabled && rawNote < splitMidiNote;
            const isSplitEdge = isSplitEnabled && rawNote === splitMidiNote;
            const black = isBlackKey(rawNote);
            const isPressed =
              Boolean(activeKeys[rawNote]) ||
              (isLower && isLowerLatch && latchedLowerNote === rawNote);

            const octOffset = isSplitEnabled
              ? isLower
                ? lowerZoneOctave * 12
                : upperZoneOctave * 12
              : globalOctave * 12;

            const naturalMidi = rawNote + octOffset;
            const rootMidiOffset = ROOT_SEMITONES[rootKey] ?? 2;
            const displayMidi = isScaleSnapEnabled
              ? quantizeNoteToScale(naturalMidi, rootMidiOffset, currentScale)
              : naturalMidi;

            const noteLabel = formatMidiNote(displayMidi);

            let keyBg: string = PocketGridRetroPalette.warmIvoryText;
            if (black) {
              keyBg = isLower ? '#202124' : '#263330';
            } else if (isLower) {
              keyBg = '#D5CFC4';
            } else {
              keyBg = idx % 2 === 0 ? '#C9B38C' : '#7BA095';
            }

            if (isPressed) {
              keyBg = isLower
                ? PocketGridRetroPalette.tungstenAmber
                : PocketGridRetroPalette.incandescentCream;
            }

            return (
              <div
                key={rawNote}
                onPointerDown={(e) => {
                  try {
                    e.currentTarget.setPointerCapture(e.pointerId);
                  } catch {}
                  handleKeyPointerDown(e, rawNote, isLower);
                }}
                onPointerMove={(e) => handleKeyPointerMove(e, rawNote, isLower)}
                onPointerUp={(e) => {
                  try {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                  } catch {}
                  handleKeyPointerUp(rawNote, isLower);
                }}
                onPointerLeave={() => handleKeyPointerUp(rawNote, isLower)}
                onPointerCancel={() => handleKeyPointerUp(rawNote, isLower)}
                style={{
                  flex: 1,
                  height: black ? '64%' : '100%',
                  background: keyBg,
                  borderLeft: isSplitEdge
                    ? `3px solid ${PocketGridRetroPalette.tungstenAmber}`
                    : '1px solid #141517',
                  borderRight: '1px solid #141517',
                  borderBottom: '3px solid #0E0F11',
                  borderRadius: '0 0 4px 4px',
                  position: 'relative',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  paddingBottom: '4px',
                  boxShadow: isPressed
                    ? `0 0 12px ${PocketGridRetroPalette.tungstenAmber}88`
                    : 'inset 0 -2px 5px rgba(0,0,0,0.28)',
                  transition: 'background 40ms ease',
                }}
              >
                {/* Dynamically Updated Note & Octave Badge (e.g. C2, D2, F#2) */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      fontSize: black ? '8px' : '9px',
                      fontWeight: 800,
                      color: isPressed
                        ? '#1E1F22'
                        : black
                        ? PocketGridRetroPalette.champagneBrass
                        : isLower
                        ? PocketGridRetroPalette.tungstenAmber
                        : '#33353A',
                      letterSpacing: '-0.02em',
                      lineHeight: 1,
                    }}
                  >
                    {noteLabel}
                  </span>
                  {!black && (
                    <div
                      style={{
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        background: isPressed
                          ? '#1E1F22'
                          : isLower
                          ? PocketGridRetroPalette.tungstenAmber
                          : PocketGridRetroPalette.vintageVfdSage,
                        opacity: isPressed ? 1 : 0.6,
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* =====================================================================
          5. [⚙ EDIT] CUSTOM SLIDER PARAMETER ASSIGNMENT MODAL
         ===================================================================== */}
      {editingBay && (
        <div
          onClick={() => setEditingBay(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 180,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '520px',
              maxWidth: '92vw',
              background: PocketGridRetroPalette.chassisGunmetal,
              border: `2px solid ${PocketGridRetroPalette.tungstenAmber}`,
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span
                style={{
                  fontWeight: 800,
                  fontSize: '14px',
                  color: PocketGridRetroPalette.tungstenAmber,
                }}
              >
                ⚙ CUSTOMIZE SLIDER SLOTS ({editingBay.toUpperCase()})
              </span>
              <button
                onClick={() => {
                  setSliderBanks((prev) => ({
                    ...prev,
                    [editingBay]: STUDIO_DEFAULT_SLIDER_BANKS[editingBay],
                  }));
                  showToast('Restored Studio Default Sliders');
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: '4px',
                  background: '#2C2318',
                  border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                  color: PocketGridRetroPalette.tungstenAmber,
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                [RESTORE STUDIO DEFAULTS]
              </button>
            </div>

            {sliderBanks[editingBay].map((currentParamId, slotIdx) => (
              <div
                key={slotIdx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 10px',
                  background: PocketGridRetroPalette.brushedTitanium,
                  borderRadius: '6px',
                  border: `1px solid ${PocketGridRetroPalette.panelBorder}`,
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: 700 }}>SLIDER SLOT {slotIdx + 1}:</span>
                <select
                  value={currentParamId}
                  onChange={(e) => {
                    const nextId = e.target.value as SynthParamId;
                    setSliderBanks((prev) => {
                      const updated = [...prev[editingBay]];
                      updated[slotIdx] = nextId;
                      return { ...prev, [editingBay]: updated };
                    });
                  }}
                  style={{
                    background: '#141517',
                    color: PocketGridRetroPalette.warmIvoryText,
                    border: `1px solid ${PocketGridRetroPalette.tungstenAmber}`,
                    borderRadius: '4px',
                    padding: '6px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {(Object.keys(ALL_ASSIGNABLE_PARAMS) as SynthParamId[]).map((pid) => (
                    <option key={pid} value={pid}>
                      [{ALL_ASSIGNABLE_PARAMS[pid].category}] {ALL_ASSIGNABLE_PARAMS[pid].label}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            <button
              onClick={() => setEditingBay(null)}
              style={{
                height: '36px',
                borderRadius: '6px',
                background: PocketGridRetroPalette.phosphorMoss,
                border: 'none',
                color: PocketGridRetroPalette.incandescentCream,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              DONE
            </button>
          </div>
        </div>
      )}

      {/* =====================================================================
          6. DEDICATED "FIND SOUNDS" FULL-SCREEN MODAL (8 POPULAR + 4 USER SLOTS + 16 SLIDERS)
         ===================================================================== */}
      {isFindSoundsOpen && (
        <FindSoundsModal
          presets={presets}
          activePreset={
            splitSoundPickerTarget === 'LOWER'
              ? lowerZonePreset
              : splitSoundPickerTarget === 'UPPER'
              ? upperZonePreset
              : activePreset
          }
          paramValues={paramValues}
          paramToCcMap={paramToCcMap}
          learningParamId={learningParamId}
          onToggleMidiLearnForParam={toggleMidiLearnForParam}
          onParamChange={handleParamChange}
          onSelectPreset={(preset) => {
            if (splitSoundPickerTarget === 'LOWER') {
              setLowerZonePreset(preset);
              lowerZonePresetRef.current = preset;
              void synthEngine.setZonePreset(0, preset);
              showToast(`Lower Split Zone: ${preset.name}`);
            } else if (splitSoundPickerTarget === 'UPPER') {
              setUpperZonePreset(preset);
              upperZonePresetRef.current = preset;
              setActivePreset(preset);
              void synthEngine.loadPreset(preset);
              void synthEngine.setZonePreset(1, preset);
              setParamValues(expandPresetParamsToAllViews(preset.params));
              showToast(`Upper Split Zone: ${preset.name}`);
            } else {
              setActivePreset(preset);
              setUpperZonePreset(preset);
              upperZonePresetRef.current = preset;
              void synthEngine.loadPreset(preset);
              void synthEngine.setZonePreset(1, preset);
              setParamValues(expandPresetParamsToAllViews(preset.params));
              showToast(`Main Preset: ${preset.name}`);
            }
          }}
          onSaveUserSlotPreset={(updatedUserPreset) => {
            setPresets((prev) =>
              prev.map((p) => (p.id === updatedUserPreset.id ? updatedUserPreset : p))
            );
            if (splitSoundPickerTarget === 'LOWER') {
              setLowerZonePreset(updatedUserPreset);
              lowerZonePresetRef.current = updatedUserPreset;
              void synthEngine.setZonePreset(0, updatedUserPreset);
            } else if (splitSoundPickerTarget === 'UPPER') {
              setUpperZonePreset(updatedUserPreset);
              upperZonePresetRef.current = updatedUserPreset;
              void synthEngine.setZonePreset(1, updatedUserPreset);
            } else {
              setActivePreset(updatedUserPreset);
              setUpperZonePreset(updatedUserPreset);
              upperZonePresetRef.current = updatedUserPreset;
              void synthEngine.setZonePreset(1, updatedUserPreset);
            }
          }}
          onToggleFavorite={(presetId) => {
            setPresets((prev) =>
              prev.map((p) => (p.id === presetId ? { ...p, favorite: !p.favorite } : p))
            );
          }}
          onImportSysexPresets={(newPresets) => {
            setPresets((prev) => [...newPresets, ...prev]);
          }}
          onOpenFineTune={(preset) => {
            setFineTuneTarget({
              soundId: preset.id,
              soundName: preset.name,
              sectionLabel: 'SOUND BROWSER PREVIEW',
            });
          }}
          onClose={() => setIsFindSoundsOpen(false)}
        />
      )}

      {/* =====================================================================
          7. DEDICATED FINE TUNE SOUND MODAL (PITCH, DYNAMICS, TIMBRE, TRANSIENT)
         ===================================================================== */}
      {fineTuneTarget && (
        <FineTuneSoundModal
          preset={presets.find((p) => p.id === fineTuneTarget.soundId) || activePreset}
          targetLabel={fineTuneTarget.sectionLabel}
          onClose={() => setFineTuneTarget(null)}
          onSaveToPreset={(updatedPreset: SynthPreset) => {
            setPresets((prev) => prev.map((p) => (p.id === updatedPreset.id ? updatedPreset : p)));
            if (lowerZonePreset.id === updatedPreset.id) {
              setLowerZonePreset(updatedPreset);
              lowerZonePresetRef.current = updatedPreset;
              void synthEngine.setZonePreset(0, updatedPreset);
            }
            if (upperZonePreset.id === updatedPreset.id) {
              setUpperZonePreset(updatedPreset);
              upperZonePresetRef.current = updatedPreset;
              void synthEngine.setZonePreset(1, updatedPreset);
            }
            if (activePreset.id === updatedPreset.id) {
              setActivePreset(updatedPreset);
              void synthEngine.loadPreset(updatedPreset);
              setParamValues(expandPresetParamsToAllViews(updatedPreset.params));
            }
            showToast(`Fine-tuned & saved: ${updatedPreset.name}`);
          }}
        />
      )}

      {/* =====================================================================
          8. CUSTOM SMART CHORD STUDIO MODAL (8 CUSTOMIZABLE CHORDS)
         ===================================================================== */}
      <CustomChordModal
        isOpen={isCustomChordModalOpen}
        onClose={() => setIsCustomChordModalOpen(false)}
        chords={smartChords}
        onSaveChords={handleSaveSmartChords}
        onAuditionChord={handleAuditionSmartChord}
      />

      {/* =====================================================================
          9. DEDICATED SEQUENCE BANK & SLOTS MANAGER MODAL
         ===================================================================== */}
      <SaveSequenceModal
        isOpen={isSaveSeqModalOpen}
        onClose={() => setIsSaveSeqModalOpen(false)}
        currentSequenceTitle={seqCustomTitle}
        activeSlotIndex={activeSeqBankSlot}
        stepCountMode={stepCountMode}
        bpm={bpm}
        sequenceBank={sequenceBank}
        onSaveToSlot={(slotIdx, customTitle) => handleSaveSequenceToBank(slotIdx, customTitle)}
        onLoadSlot={handleLoadSequenceFromBank}
        onRenameSlot={handleRenameSequenceSlot}
        onClearSlot={handleClearSequenceSlot}
      />
    </div>
  );
};
